import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { AdminOperationId, ItemIdSchema, UserIdSchema } from "../id"
import { UserDenormalizedWithoutLook, UserStatus } from "./user"
import { WelcomeDeliveryMasterMeta } from "./welcome-delivery-master"
import { BulkDeliveryMasterKind, BulkDeliveryMasterStatus } from "./bulk-delivery-master"
import { Period } from "./common"
import { DocumentStatus } from "../common"
import { PinnedStoryUserAudience } from "./pinned-story-user"
import { ViolationReportStatusSchema } from "./violation-report"
import { SpaceUserRole } from "./space-user"

/**
 * 管理者操作ログの種別。
 * kind-value パターンで、様々な監査ログを格納できるようにする。
 */
export const AdminOperationKind = {
  updateUserProfile: "update-user-profile",
  updateUserStatus: "update-user-status",
  updateUserVerified: "update-user-verified",
  updateUserIcon: "update-user-icon",
  deleteUserIcon: "delete-user-icon",
  forceWithdrawal: "force-withdrawal",
  deleteUserStory: "delete-user-story",
  // ブロック管理
  blockUser: "block-user",
  unblockUser: "unblock-user",
  // 管理者によるサブスクリプション付与
  grantUserSubscriptionByAdmin: "grant-user-subscription-by-admin",
  // デフォルトホロスーツ
  createInitialLook: "create-initial-look",
  updateInitialLook: "update-initial-look",
  deleteInitialLook: "delete-initial-look",
  // ホームバナー
  createHomeBanner: "create-home-banner",
  updateHomeBanner: "update-home-banner",
  deleteHomeBanner: "delete-home-banner",
  // ホームピックアップ
  createHomePickup: "create-home-pickup",
  deleteHomePickup: "delete-home-pickup",
  // ホームライブピン
  createHomeLivePin: "create-home-live-pin",
  updateHomeLivePin: "update-home-live-pin",
  deleteHomeLivePin: "delete-home-live-pin",
  // 画像管理
  createManagedImage: "create-managed-image",
  replaceManagedImage: "replace-managed-image",
  deleteManagedImage: "delete-managed-image",
  updateManagedImageUsageTypes: "update-managed-image-usage-types",
  // 抽選のお題
  createTopicSuggestion: "create-topic-suggestion",
  updateTopicSuggestion: "update-topic-suggestion",
  deleteTopicSuggestion: "delete-topic-suggestion",
  // NGワード
  createGlobalNgWord: "create-global-ng-word",
  updateGlobalNgWord: "update-global-ng-word",
  deleteGlobalNgWord: "delete-global-ng-word",
  // パワー管理
  createPower: "create-power",
  updatePower: "update-power",
  deletePower: "delete-power",
  // フォローしませんかユーザー
  createRecommendedUser: "create-recommended-user",
  deleteRecommendedUser: "delete-recommended-user",
  // 許可ドメイン
  createAllowedDomain: "create-allowed-domain",
  deleteAllowedDomain: "delete-allowed-domain",
  // 一括配信
  createBulkDeliveryMaster: "create-bulk-delivery-master",
  updateBulkDeliveryMasterStatus: "update-bulk-delivery-master-status",
  updateBulkDeliveryMasterContent: "update-bulk-delivery-master-content",
  updateBulkDeliveryMasterDeletion: "update-bulk-delivery-master-deletion",
  updateBulkDeliveryMasterTargetSetting: "update-bulk-delivery-master-target-setting",
  deleteBulkDeliveryMaster: "delete-bulk-delivery-master",
  // 新規ユーザー配布マスタ
  createWelcomeDeliveryMaster: "create-welcome-delivery-master",
  updateWelcomeDeliveryMaster: "update-welcome-delivery-master",
  // お知らせ
  createSystemNotification: "create-system-notification",
  updateSystemNotification: "update-system-notification",
  deleteSystemNotification: "delete-system-notification",
  // ショップバナー
  createShopBanner: "create-shop-banner",
  updateShopBanner: "update-shop-banner",
  deleteShopBanner: "delete-shop-banner",
  // ショップカスタムタグ
  createShopCustomTag: "create-shop-custom-tag",
  updateShopCustomTag: "update-shop-custom-tag",
  deleteShopCustomTag: "delete-shop-custom-tag",
  // ショップTOP編成
  updateShopTopCompositionFirst: "update-shop-top-composition-first",
  updateShopTopCompositionList: "update-shop-top-composition-list",
  // GeoIP CIDR管理
  createGeoIpCidrOverride: "create-geo-ip-cidr-override",
  deleteGeoIpCidrOverride: "delete-geo-ip-cidr-override",
  // 管理者
  registerAdmin: "register-admin",
  updateAdmin: "update-admin",
  deleteAdmin: "delete-admin",
  inviteAdmin: "invite-admin",
  deleteAdminInvite: "delete-admin-invite",
  // アプリ設定
  setAppConfigRequirement: "set-app-config-requirement",
  deleteAppConfigRequirement: "delete-app-config-requirement",
  setAppConfigTermsAgreement: "set-app-config-terms-agreement",
  setAppConfigGlobalNavigation: "set-app-config-global-navigation",
  // 変換レート管理（パワー→LIVEポイント / LIVEポイント→無償コイン / 無償パワー→期限付き無償コイン 統合）
  setConversionRate: "set-conversion-rate",
  // ユーザー個別レート管理
  setUserConversionRate: "set-user-conversion-rate",
  deleteUserConversionRate: "delete-user-conversion-rate",
  // LIVEポイント加減算
  updateLivePoints: "update-live-points",
  // LIVEポイント交換停止/解除
  updateLivePointExchangeBlock: "update-live-point-exchange-block",
  // LIVEポイント変換ブロック（LIVE単位）
  updateLivePointConversionBlock: "update-live-point-conversion-block",
  // ライブコメント管理
  deleteLiveComment: "delete-live-comment",
  // お題投稿（抽選参加者）管理
  deleteSelectionParticipant: "delete-selection-participant",
  // メンテナンス管理
  createMaintenanceSchedule: "create-maintenance-schedule",
  updateMaintenanceSchedule: "update-maintenance-schedule",
  deleteMaintenanceSchedule: "delete-maintenance-schedule",
  duplicateMaintenanceSchedule: "duplicate-maintenance-schedule",
  createMaintenanceInternalUser: "create-maintenance-internal-user",
  updateMaintenanceInternalUser: "update-maintenance-internal-user",
  deleteMaintenanceInternalUser: "delete-maintenance-internal-user",
  // システム運用 / 運用補助
  updateSpaceThumbnailSetting: "update-space-thumbnail-setting",
  updateVrmAccountImageSetting: "update-vrm-account-image-setting",
  requestToGenerateActiveSpaceThumbnails: "request-to-generate-active-space-thumbnails",
  requestToGenerateLiveThumbnails: "request-to-generate-live-thumbnails",
  sendDebugPush: "send-debug-push",
  syncCoinBalanceCache: "sync-coin-balance-cache",
  recoverAlgolia: "recover-algolia",
  // コイン管理
  insertCoinPack: "insert-coin-pack",
  revertCoinPack: "revert-coin-pack",
  removeCoinPack: "remove-coin-pack",
  updateCoinPack: "update-coin-pack",
  // サブスクリプション
  createSubscriptionPlan: "create-subscription-plan",
  updateSubscriptionPlan: "update-subscription-plan",
  deleteSubscriptionPlan: "delete-subscription-plan",
  createSubscriptionProduct: "create-subscription-product",
  updateSubscriptionProduct: "update-subscription-product",
  deleteSubscriptionProduct: "delete-subscription-product",
  cancelIABSubscriptions: "cancel-iab-subscriptions",
  // サブスクリプションアイテム付与マスタ
  createSubscriptionItemGrantMaster: "create-subscription-item-grant-master",
  updateSubscriptionItemGrantMaster: "update-subscription-item-grant-master",
  deleteSubscriptionItemGrantMaster: "delete-subscription-item-grant-master",
  // ブランド管理
  createBrand: "create-brand",
  updateBrand: "update-brand",
  deleteBrand: "delete-brand",
  // アイテム管理
  createItem: "create-item",
  updateItem: "update-item",
  deleteItem: "delete-item",
  // アイテム配信（販売期間）
  createItemDistribution: "create-item-distribution",
  updateItemDistribution: "update-item-distribution",
  deleteItemDistribution: "delete-item-distribution",
  // セール管理
  createSale: "create-sale",
  updateSale: "update-sale",
  deleteSale: "delete-sale",
  // ショップ検索おすすめアイテム
  updateShopSearchRecommendedItems: "update-shop-search-recommended-items",
  // スペース管理
  createSpaceByAdmin: "create-space",
  updateSpaceByAdmin: "update-space",
  deleteSpaceByAdmin: "delete-space",
  addUserToSpaceByAdmin: "add-user-to-space",
  removeUserFromSpaceByAdmin: "remove-user-from-space",
  updateSpaceUserRoleByAdmin: "update-space-user-role",
  stopLiveByAdmin: "stop-live",
  updateLiveByAdmin: "update-live",
  // スペースの背景
  createSpaceBackground: "create-space-background",
  updateSpaceBackground: "update-space-background",
  deleteSpaceBackground: "delete-space-background",
  // スペースのBGM
  createSpaceBGM: "create-space-bgm",
  updateSpaceBGM: "update-space-bgm",
  deleteSpaceBGM: "delete-space-bgm",
  // スタンプカード
  createStampCard: "create-stamp-card",
  updateStampCard: "update-stamp-card",
  deleteStampCard: "delete-stamp-card",
  createStampLane: "create-stamp-lane",
  updateStampLane: "update-stamp-lane",
  deleteStampLane: "delete-stamp-lane",
  createStampLaneReward: "create-stamp-lane-reward",
  updateStampLaneReward: "update-stamp-lane-reward",
  deleteStampLaneReward: "delete-stamp-lane-reward",
  createStampMission: "create-stamp-mission",
  updateStampMission: "update-stamp-mission",
  deleteStampMission: "delete-stamp-mission",
  // スタンプミッション進捗の強制変更
  forceUpdateStampMissionProgress: "force-update-stamp-mission-progress",
  // 通報管理
  updateViolationReport: "update-violation-report",
  // ストーリーズピン留め
  createPinnedStoryUser: "create-pinned-story-user",
  updatePinnedStoryUser: "update-pinned-story-user",
  deletePinnedStoryUser: "delete-pinned-story-user",
  // ライブジャンル
  createLiveGenre: "create-live-genre",
  updateLiveGenre: "update-live-genre",
  updateLiveGenrePriority: "update-live-genre-priority",
  // 定型ニックネーム / 二つ名 / 修飾語マスタ
  createPredefinedNickname: "create-predefined-nickname",
  deletePredefinedNickname: "delete-predefined-nickname",
  createPredefinedAlias: "create-predefined-alias",
  deletePredefinedAlias: "delete-predefined-alias",
  createPredefinedAliasQualifier: "create-predefined-alias-qualifier",
  deletePredefinedAliasQualifier: "delete-predefined-alias-qualifier",
  // 管理者によるホロスーツ変更
  updateUserLookByAdmin: "update-user-look-by-admin",
  // 管理ツールからのフォロー操作
  createUserFollow: "create-user-follow",
  deleteUserFollow: "delete-user-follow",
  // ユーザーインベントリ管理
  grantUserInventoryByAdmin: "grant-user-inventory-by-admin",
  deleteUserInventoryByAdmin: "delete-user-inventory-by-admin",
  // 機能フラグ
  updateFeatureFlag: "update-feature-flag",
  addFeatureFlagAllowedUser: "add-feature-flag-allowed-user",
  removeFeatureFlagAllowedUser: "remove-feature-flag-allowed-user",
  // VRM 運営指定NG
  banUserVrmByOperator: "ban-user-vrm-by-operator",
  unbanUserVrmByOperator: "unban-user-vrm-by-operator",
  // itemId 起点の運営指定NG解除（登録ドキュメントに依存しない）
  unbanVrmByItemId: "unban-vrm-by-item-id",
  // LIVEポイント付与タスク手動投入
  enqueueGrantLivePoints: "enqueue-grant-live-points",
  // 期限付き無償コイン配布 手動実行
  enqueueGrantExpiringFreeCoins: "enqueue-grant-expiring-free-coins",
} as const

export type AdminOperationKind = typeof AdminOperationKind[keyof typeof AdminOperationKind]

/**
 * 操作対象の種別。
 * target.kind + target.value でフィルタすることで、特定のリソースに対する操作履歴を取得できる。
 */
export const AdminOperationTargetKind = {
  user: "user",
  initialLook: "initial-look",
  homeBanner: "home-banner",
  homePickup: "home-pickup",
  homeLivePin: "home-live-pin",
  managedImage: "managed-image",
  topicSuggestion: "topic-suggestion",
  globalNgWord: "global-ng-word",
  power: "power",
  recommendedUser: "recommended-user",
  allowedDomain: "allowed-domain",
  bulkDeliveryMaster: "bulk-delivery-master",
  welcomeDeliveryMaster: "welcome-delivery-master",
  systemNotification: "system-notification",
  shopBanner: "shop-banner",
  shopCustomTag: "shop-custom-tag",
  shopTopComposition: "shop-top-composition",
  geoIpCidr: "geo-ip-cidr",
  admin: "admin",
  adminInvite: "admin-invite",
  appConfig: "app-config",
  maintenanceSchedule: "maintenance-schedule",
  maintenanceInternalUser: "maintenance-internal-user",
  liveComment: "live-comment",
  selectionParticipant: "selection-participant",
  system: "system",
  coinPack: "coin-pack",
  subscriptionPlan: "subscription-plan",
  subscriptionProduct: "subscription-product",
  subscriptionItemGrantMaster: "subscription-item-grant-master",
  brand: "brand",
  item: "item",
  itemDistribution: "item-distribution",
  sale: "sale",
  shopSearchRecommendedItems: "shop-search-recommended-items",
  space: "space",
  live: "live",
  spaceBackground: "space-background",
  spaceBGM: "space-bgm",
  stampCard: "stamp-card",
  stampLane: "stamp-lane",
  stampLaneReward: "stamp-lane-reward",
  stampMission: "stamp-mission",
  violationReport: "violation-report",
  pinnedStoryUser: "pinned-story-user",
  liveGenre: "live-genre",
  predefinedNickname: "predefined-nickname",
  predefinedAlias: "predefined-alias",
  predefinedAliasQualifier: "predefined-alias-qualifier",
  featureFlag: "feature-flag",
  userInventory: "user-inventory",
  vrmRegistration: "vrm-registration",
  vrmNgItem: "vrm-ng-item",
} as const

export type AdminOperationTargetKind = typeof AdminOperationTargetKind[keyof typeof AdminOperationTargetKind]

const AdminOperationTargetUser = z.object({
  kind: z.literal(AdminOperationTargetKind.user),
  value: UserDenormalizedWithoutLook,
}).describe("操作対象（ユーザー）。value.id でフィルタすることで、特定のユーザーに対する操作履歴を取得できる。")

const AdminOperationTargetInitialLook = z.object({
  kind: z.literal(AdminOperationTargetKind.initialLook),
  value: z.object({
    id: z.string().describe("ItemId"),
  }),
}).describe("操作対象（デフォルトホロスーツ）。value.id でフィルタすることで、特定のホロスーツに対する操作履歴を取得できる。")

const AdminOperationTargetHomeBanner = z.object({
  kind: z.literal(AdminOperationTargetKind.homeBanner),
  value: z.object({
    id: z.string().describe("HomeBannerId"),
    title: z.string().describe("操作時点のバナータイトル"),
  }),
}).describe("操作対象（ホームバナー）。value.id でフィルタすることで、特定のバナーに対する操作履歴を取得できる。")

const AdminOperationTargetHomePickup = z.object({
  kind: z.literal(AdminOperationTargetKind.homePickup),
  value: z.object({
    id: z.string().describe("HomePickupId"),
  }),
}).describe("操作対象（ホームピックアップ）。value.id でフィルタすることで、特定のピックアップに対する操作履歴を取得できる。")

const AdminOperationTargetHomeLivePin = z.object({
  kind: z.literal(AdminOperationTargetKind.homeLivePin),
  value: z.object({
    id: z.string().describe("HomeLivePinId"),
    space_key: z.string().describe("SpaceKey"),
  }),
}).describe("操作対象（ホームライブピン）。value.id でフィルタすることで、特定のライブピンに対する操作履歴を取得できる。")

const AdminOperationTargetManagedImage = z.object({
  kind: z.literal(AdminOperationTargetKind.managedImage),
  value: z.object({
    id: z.string().describe("ManagedImageId"),
  }),
}).describe("操作対象（画像）。value.id でフィルタすることで、特定の画像に対する操作履歴を取得できる。")

const AdminOperationTargetTopicSuggestion = z.object({
  kind: z.literal(AdminOperationTargetKind.topicSuggestion),
  value: z.object({
    id: z.string().describe("TopicSuggestionId"),
    text: z.string().describe("操作時点のお題テキスト"),
  }),
}).describe("操作対象（抽選のお題）。value.id でフィルタすることで、特定のお題に対する操作履歴を取得できる。")

const AdminOperationTargetGlobalNgWord = z.object({
  kind: z.literal(AdminOperationTargetKind.globalNgWord),
  value: z.object({
    id: z.string().describe("GlobalNgWordId"),
  }),
}).describe("操作対象（NGワード）。value.id でフィルタすることで、特定のNGワードに対する操作履歴を取得できる。")

const AdminOperationTargetPower = z.object({
  kind: z.literal(AdminOperationTargetKind.power),
  value: z.object({
    id: z.string().describe("PowerId"),
  }),
}).describe("操作対象（パワー）。value.id でフィルタすることで、特定のパワーに対する操作履歴を取得できる。")

const AdminOperationTargetRecommendedUser = z.object({
  kind: z.literal(AdminOperationTargetKind.recommendedUser),
  value: z.object({
    id: z.string().describe("UserId"),
  }),
}).describe("操作対象（フォローしませんかユーザー）。value.id でフィルタすることで、特定のユーザーに対する操作履歴を取得できる。")

const AdminOperationTargetAllowedDomain = z.object({
  kind: z.literal(AdminOperationTargetKind.allowedDomain),
  value: z.object({
    id: z.string().describe("AllowedDomainId"),
    domain: z.string().describe("操作時点のドメイン名"),
  }),
}).describe("操作対象（許可ドメイン）。value.id でフィルタすることで、特定の許可ドメインに対する操作履歴を取得できる。")

const AdminOperationTargetBulkDeliveryMaster = z.object({
  kind: z.literal(AdminOperationTargetKind.bulkDeliveryMaster),
  value: z.object({
    id: z.string().describe("BulkDeliveryMasterId"),
    kind: z.enum(BulkDeliveryMasterKind).describe("配信種別"),
  }),
}).describe("操作対象（一括配信マスタ）。value.id でフィルタすることで、特定の配信マスタに対する操作履歴を取得できる。")

const AdminOperationTargetWelcomeDeliveryMaster = z.object({
  kind: z.literal(AdminOperationTargetKind.welcomeDeliveryMaster),
  value: z.object({
    id: z.string().describe("WelcomeDeliveryMasterId"),
  }),
}).describe("操作対象（新規ユーザー配布マスタ）。value.id でフィルタすることで、特定のマスタに対する操作履歴を取得できる。")

const AdminOperationTargetSystemNotification = z.object({
  kind: z.literal(AdminOperationTargetKind.systemNotification),
  value: z.object({
    id: z.string().describe("SystemNotificationId"),
    title: z.string().describe("操作時点のお知らせタイトル"),
  }),
}).describe("操作対象（お知らせ）。value.id でフィルタすることで、特定のお知らせに対する操作履歴を取得できる。")

const AdminOperationTargetShopBanner = z.object({
  kind: z.literal(AdminOperationTargetKind.shopBanner),
  value: z.object({
    id: z.string().describe("ShopBannerId"),
  }),
}).describe("操作対象（ショップバナー）。value.id でフィルタすることで、特定のバナーに対する操作履歴を取得できる。")

const AdminOperationTargetShopCustomTag = z.object({
  kind: z.literal(AdminOperationTargetKind.shopCustomTag),
  value: z.object({
    id: z.string().describe("ShopCustomTagId"),
    title: z.string().describe("操作時点のタグタイトル"),
  }),
}).describe("操作対象（ショップカスタムタグ）。value.id でフィルタすることで、特定のカスタムタグに対する操作履歴を取得できる。")

const AdminOperationTargetShopTopComposition = z.object({
  kind: z.literal(AdminOperationTargetKind.shopTopComposition),
  value: z.object({
    id: z.string().describe("編成種別（first または list）"),
  }),
}).describe("操作対象（ショップTOP編成）。value.id でフィルタすることで、特定の編成に対する操作履歴を取得できる。")

const AdminOperationTargetGeoIpCidr = z.object({
  kind: z.literal(AdminOperationTargetKind.geoIpCidr),
  value: z.object({
    cidr: z.string().describe("CIDR表記"),
  }),
}).describe("操作対象（GeoIP CIDRオーバーライド）。value.cidr でフィルタすることで、特定のCIDRに対する操作履歴を取得できる。")

const AdminOperationTargetAdmin = z.object({
  kind: z.literal(AdminOperationTargetKind.admin),
  value: z.object({
    id: z.string().describe("AdminId"),
  }),
}).describe("操作対象（管理者）。value.id でフィルタすることで、特定の管理者に対する操作履歴を取得できる。")

const AdminOperationTargetAdminInvite = z.object({
  kind: z.literal(AdminOperationTargetKind.adminInvite),
  value: z.object({
    id: z.string().describe("AdminInviteId"),
  }),
}).describe("操作対象（管理者招待）。value.id でフィルタすることで、特定の招待に対する操作履歴を取得できる。")

const AdminOperationTargetAppConfig = z.object({
  kind: z.literal(AdminOperationTargetKind.appConfig),
  value: z.object({
    id: z.string().describe("AppConfig ドキュメントID（例: requirement, terms, global-navigation）"),
  }),
}).describe("操作対象（アプリ設定）。value.id でフィルタすることで、特定のアプリ設定に対する操作履歴を取得できる。")

const AdminOperationTargetMaintenanceSchedule = z.object({
  kind: z.literal(AdminOperationTargetKind.maintenanceSchedule),
  value: z.object({
    id: z.string().describe("MaintenanceScheduleEntryId"),
  }),
}).describe("操作対象（メンテナンススケジュール）。value.id でフィルタすることで、特定のスケジュールに対する操作履歴を取得できる。")

const AdminOperationTargetMaintenanceInternalUser = z.object({
  kind: z.literal(AdminOperationTargetKind.maintenanceInternalUser),
  value: z.object({
    id: z.string().describe("UserId"),
  }),
}).describe("操作対象（内部確認ユーザー）。value.id でフィルタすることで、特定の内部確認ユーザーに対する操作履歴を取得できる。")

const AdminOperationTargetLiveComment = z.object({
  kind: z.literal(AdminOperationTargetKind.liveComment),
  value: z.object({
    space_key: z.string().describe("SpaceKey"),
    live_id: z.string().describe("LiveId"),
    comment_id: z.string().describe("LiveCommentId"),
  }),
}).describe("操作対象（ライブコメント）。value でフィルタすることで、特定のコメントに対する操作履歴を取得できる。")

const AdminOperationTargetSelectionParticipant = z.object({
  kind: z.literal(AdminOperationTargetKind.selectionParticipant),
  value: z.object({
    space_key: z.string().describe("SpaceKey"),
    live_id: z.string().describe("LiveId"),
    selection_id: z.string().describe("SelectionId"),
    participant_id: z.string().describe("SelectionParticipantId"),
  }),
}).describe("操作対象（お題投稿・抽選参加者）。value でフィルタすることで、特定の参加者に対する操作履歴を取得できる。")

const AdminOperationTargetSystemIds = ["space-thumbnail-setting", "vrm-account-image-setting", "debug-push", "recover-algolia", "cancel-iab-subscriptions", "enqueue-grant-live-points", "expiring-free-coin-grant"] as const

const AdminOperationTargetSystem = z.object({
  kind: z.literal(AdminOperationTargetKind.system),
  value: z.object({
    id: z.enum(AdminOperationTargetSystemIds).describe("システム操作対象を識別する文字列。特定の Firestore ドキュメント等のリソース ID ではなく、`(target.kind, target.value.id)` の複合インデックスで運用補助系操作の履歴を絞り込むためのキーとして利用する。"),
  }),
}).describe("操作対象（システム全体）。特定のリソースに紐付かない運用補助系操作で使用する。専用 target を切らずに `(target.kind, target.value.id)` のインデックスを共用することで、運用補助系操作の履歴を target でまとめて絞り込めるようにしている。")


const AdminOperationTargetCoinPack = z.object({
  kind: z.literal(AdminOperationTargetKind.coinPack),
  value: z.object({
    id: z.string().describe("CoinPackId"),
    store: z.number().describe("PlatformStore"),
  }),
}).describe("操作対象（コインパック）。value.id でフィルタすることで、特定のコインパックに対する操作履歴を取得できる。")

const AdminOperationTargetSubscriptionPlan = z.object({
  kind: z.literal(AdminOperationTargetKind.subscriptionPlan),
  value: z.object({
    id: z.string().describe("SubscriptionPlanId"),
  }),
}).describe("操作対象（サブスクリプションプラン）。value.id でフィルタすることで、特定のプランに対する操作履歴を取得できる。")

const AdminOperationTargetSubscriptionProduct = z.object({
  kind: z.literal(AdminOperationTargetKind.subscriptionProduct),
  value: z.object({
    id: z.string().describe("SubscriptionProductId"),
    plan_id: z.string().describe("SubscriptionPlanId"),
  }),
}).describe("操作対象（サブスクリプション商品）。value.id でフィルタすることで、特定の商品に対する操作履歴を取得できる。")

const AdminOperationTargetSubscriptionItemGrantMaster = z.object({
  kind: z.literal(AdminOperationTargetKind.subscriptionItemGrantMaster),
  value: z.object({
    id: z.string().describe("SubscriptionItemGrantMasterId"),
  }),
}).describe("操作対象（サブスクリプションアイテム付与マスタ）。value.id でフィルタすることで、特定のマスタに対する操作履歴を取得できる。")

const AdminOperationTargetBrand = z.object({
  kind: z.literal(AdminOperationTargetKind.brand),
  value: z.object({
    id: z.string().describe("BrandId"),
  }),
}).describe("操作対象（ブランド）。value.id でフィルタすることで、特定のブランドに対する操作履歴を取得できる。")

const AdminOperationTargetItem = z.object({
  kind: z.literal(AdminOperationTargetKind.item),
  value: z.object({
    id: z.string().describe("ItemId"),
  }),
}).describe("操作対象（アイテム）。value.id でフィルタすることで、特定のアイテムに対する操作履歴を取得できる。")

const AdminOperationTargetItemDistribution = z.object({
  kind: z.literal(AdminOperationTargetKind.itemDistribution),
  value: z.object({
    id: z.string().describe("ItemDistributionId"),
    item_id: z.string().describe("親アイテムの ItemId"),
  }),
}).describe("操作対象（アイテム配信＝販売期間）。value.id でフィルタすることで、特定の販売期間に対する操作履歴を取得できる。")

const AdminOperationTargetSale = z.object({
  kind: z.literal(AdminOperationTargetKind.sale),
  value: z.object({
    id: z.string().describe("SaleId"),
  }),
}).describe("操作対象（セール）。value.id でフィルタすることで、特定のセールに対する操作履歴を取得できる。")

const AdminOperationTargetShopSearchRecommendedItems = z.object({
  kind: z.literal(AdminOperationTargetKind.shopSearchRecommendedItems),
  value: z.object({}),
}).describe("操作対象（ショップ検索のおすすめアイテム設定）。シングルトン的な対象として扱う。")

const AdminOperationTargetSpace = z.object({
  kind: z.literal(AdminOperationTargetKind.space),
  value: z.object({
    id: z.string().describe("SpaceKey"),
    name: z.string().describe("スペース名"),
  }),
}).describe("操作対象（スペース）。value.id でフィルタすることで、特定のスペースに対する操作履歴を取得できる。")

const AdminOperationTargetLive = z.object({
  kind: z.literal(AdminOperationTargetKind.live),
  value: z.object({
    id: z.string().describe("LiveId"),
    space_key: z.string().describe("SpaceKey"),
    space_name: z.string().describe("スペース名"),
  }),
}).describe("操作対象（ライブ）。value.id でフィルタすることで、特定のライブに対する操作履歴を取得できる。")

const AdminOperationTargetSpaceBackground = z.object({
  kind: z.literal(AdminOperationTargetKind.spaceBackground),
  value: z.object({
    id: z.string().describe("SpaceBackgroundId"),
  }),
}).describe("操作対象（スペースの背景）。value.id でフィルタすることで、特定の背景に対する操作履歴を取得できる。")

const AdminOperationTargetSpaceBGM = z.object({
  kind: z.literal(AdminOperationTargetKind.spaceBGM),
  value: z.object({
    id: z.string().describe("SpaceBGMId"),
  }),
}).describe("操作対象（スペースのBGM）。value.id でフィルタすることで、特定のBGMに対する操作履歴を取得できる。")

const AdminOperationTargetStampCard = z.object({
  kind: z.literal(AdminOperationTargetKind.stampCard),
  value: z.object({
    id: z.string().describe("StampCardId"),
    name: z.string().describe("操作時点のカード名"),
  }),
}).describe("操作対象（スタンプカード）。value.id でフィルタすることで、特定のカードに対する操作履歴を取得できる。")

const AdminOperationTargetStampLane = z.object({
  kind: z.literal(AdminOperationTargetKind.stampLane),
  value: z.object({
    id: z.string().describe("StampLaneId"),
    card_id: z.string().describe("StampCardId"),
  }),
}).describe("操作対象（スタンプレーン）。value.id でフィルタすることで、特定のレーンに対する操作履歴を取得できる。")

const AdminOperationTargetStampLaneReward = z.object({
  kind: z.literal(AdminOperationTargetKind.stampLaneReward),
  value: z.object({
    id: z.string().describe("StampLaneRewardId"),
    card_id: z.string().describe("StampCardId"),
    lane_id: z.string().describe("StampLaneId"),
  }),
}).describe("操作対象（スタンプレーン報酬）。value.id でフィルタすることで、特定の報酬に対する操作履歴を取得できる。")

const AdminOperationTargetStampMission = z.object({
  kind: z.literal(AdminOperationTargetKind.stampMission),
  value: z.object({
    id: z.string().describe("StampMissionId"),
    card_id: z.string().describe("StampCardId"),
  }),
}).describe("操作対象（スタンプミッション）。value.id でフィルタすることで、特定のミッションに対する操作履歴を取得できる。")

const AdminOperationTargetViolationReport = z.object({
  kind: z.literal(AdminOperationTargetKind.violationReport),
  value: z.object({
    id: z.string().describe("ViolationReportId"),
  }),
}).describe("操作対象（通報）。value.id でフィルタすることで、特定の通報に対する操作履歴を取得できる。")

const AdminOperationTargetPinnedStoryUser = z.object({
  kind: z.literal(AdminOperationTargetKind.pinnedStoryUser),
  value: z.object({
    id: z.string().describe("PinnedStoryUserId"),
  }),
}).describe("操作対象（ストーリーズピン留め）。value.id でフィルタすることで、特定のピン留めに対する操作履歴を取得できる。")

const AdminOperationTargetLiveGenre = z.object({
  kind: z.literal(AdminOperationTargetKind.liveGenre),
  value: z.object({
    id: z.string().describe("LiveGenreId"),
  }),
}).describe("操作対象（ライブジャンル）。value.id でフィルタすることで、特定のジャンルに対する操作履歴を取得できる。")

const AdminOperationTargetPredefinedNickname = z.object({
  kind: z.literal(AdminOperationTargetKind.predefinedNickname),
  value: z.object({
    id: z.string().describe("PredefinedNicknameId"),
    name: z.string().describe("定型ニックネームの文字列（非正規化・操作時点のスナップショット）"),
  }),
}).describe("操作対象（定型ニックネーム）。value.id でフィルタすることで、特定のニックネームに対する操作履歴を取得できる。")

const AdminOperationTargetPredefinedAlias = z.object({
  kind: z.literal(AdminOperationTargetKind.predefinedAlias),
  value: z.object({
    id: z.string().describe("PredefinedAliasId"),
    name: z.string().describe("定型二つ名の文字列（非正規化・操作時点のスナップショット）"),
  }),
}).describe("操作対象（定型二つ名）。value.id でフィルタすることで、特定の二つ名に対する操作履歴を取得できる。")

const AdminOperationTargetPredefinedAliasQualifier = z.object({
  kind: z.literal(AdminOperationTargetKind.predefinedAliasQualifier),
  value: z.object({
    id: z.string().describe("PredefinedAliasQualifierId"),
    name: z.string().describe("定型修飾語の文字列（非正規化・操作時点のスナップショット）"),
  }),
}).describe("操作対象（定型修飾語）。value.id でフィルタすることで、特定の修飾語に対する操作履歴を取得できる。")

const AdminOperationTargetFeatureFlag = z.object({
  kind: z.literal(AdminOperationTargetKind.featureFlag),
  value: z.object({
    id: z.string().describe("FeatureFlag ドキュメントID"),
  }),
}).describe("操作対象（機能フラグ）。value.id でフィルタすることで、特定の機能フラグに対する操作履歴を取得できる。")

const AdminOperationTargetUserInventory = z.object({
  kind: z.literal(AdminOperationTargetKind.userInventory),
  value: z.object({
    user_id: z.string().describe("対象ユーザーのID"),
    item_id: z.string().describe("対象アイテムのID"),
    item_name: z.string().describe("アイテム名（操作時点のスナップショット）"),
  }),
}).describe("操作対象（ユーザーインベントリ）。value.user_id + value.item_id でフィルタすることで、特定のユーザーアイテムに対する操作履歴を取得できる。")

const AdminOperationTargetVrmRegistration = z.object({
  kind: z.literal(AdminOperationTargetKind.vrmRegistration),
  value: z.object({
    user_id: z.string().describe("対象ユーザーのID"),
    registration_id: z.string().describe("VRM 登録ドキュメントの ID"),
    vrm_name: z.string().describe("VRM 名（操作時点のスナップショット）"),
  }),
}).describe("操作対象（VRM 登録）。value.user_id + value.registration_id でフィルタすることで、特定の VRM 登録に対する操作履歴を取得できる。")

const AdminOperationTargetVrmNgItem = z.object({
  kind: z.literal(AdminOperationTargetKind.vrmNgItem),
  value: z.object({
    item_id: z.string().describe("運営指定NG（グローバルNGリスト）の itemId。ドキュメントID と同値。"),
    vrm_name: z.string().optional().describe("VRM 名（NG設定時点のスナップショット・参照用）"),
  }),
}).describe("操作対象（VRM 運営指定NG itemId）。value.item_id でフィルタすることで、特定の itemId に対する操作履歴を取得できる。")

const AdminOperationTarget = z.union([
  AdminOperationTargetUser,
  AdminOperationTargetInitialLook,
  AdminOperationTargetHomeBanner,
  AdminOperationTargetHomePickup,
  AdminOperationTargetHomeLivePin,
  AdminOperationTargetManagedImage,
  AdminOperationTargetTopicSuggestion,
  AdminOperationTargetGlobalNgWord,
  AdminOperationTargetPower,
  AdminOperationTargetRecommendedUser,
  AdminOperationTargetAllowedDomain,
  AdminOperationTargetBulkDeliveryMaster,
  AdminOperationTargetWelcomeDeliveryMaster,
  AdminOperationTargetSystemNotification,
  AdminOperationTargetShopBanner,
  AdminOperationTargetShopCustomTag,
  AdminOperationTargetShopTopComposition,
  AdminOperationTargetGeoIpCidr,
  AdminOperationTargetAdmin,
  AdminOperationTargetAdminInvite,
  AdminOperationTargetAppConfig,
  AdminOperationTargetMaintenanceSchedule,
  AdminOperationTargetMaintenanceInternalUser,
  AdminOperationTargetLiveComment,
  AdminOperationTargetSelectionParticipant,
  AdminOperationTargetSystem,
  AdminOperationTargetCoinPack,
  AdminOperationTargetSubscriptionPlan,
  AdminOperationTargetSubscriptionProduct,
  AdminOperationTargetSubscriptionItemGrantMaster,
  AdminOperationTargetBrand,
  AdminOperationTargetItem,
  AdminOperationTargetItemDistribution,
  AdminOperationTargetSale,
  AdminOperationTargetShopSearchRecommendedItems,
  AdminOperationTargetSpace,
  AdminOperationTargetLive,
  AdminOperationTargetSpaceBackground,
  AdminOperationTargetSpaceBGM,
  AdminOperationTargetStampCard,
  AdminOperationTargetStampLane,
  AdminOperationTargetStampLaneReward,
  AdminOperationTargetStampMission,
  AdminOperationTargetViolationReport,
  AdminOperationTargetPinnedStoryUser,
  AdminOperationTargetLiveGenre,
  AdminOperationTargetPredefinedNickname,
  AdminOperationTargetPredefinedAlias,
  AdminOperationTargetPredefinedAliasQualifier,
  AdminOperationTargetFeatureFlag,
  AdminOperationTargetUserInventory,
  AdminOperationTargetVrmRegistration,
  AdminOperationTargetVrmNgItem,
]).describe("操作対象。kind + value.id でフィルタすることで、特定のリソースに対する操作履歴を取得できる。")

export type AdminOperationTarget = z.infer<typeof AdminOperationTarget>

const UpdateUserProfileOperationValue = z.object({
  user_id: UserIdSchema,
  changes: z.record(z.string(), z.unknown()).describe("変更されたフィールドと値のマップ"),
}).describe("プロフィール更新操作の詳細")

export type UpdateUserProfileOperationValue = z.infer<typeof UpdateUserProfileOperationValue>

const UpdateUserStatusOperationValue = z.object({
  user_id: UserIdSchema,
  status: UserStatus,
  description: z.string(),
}).describe("ユーザーステータス変更操作の詳細")

export type UpdateUserStatusOperationValue = z.infer<typeof UpdateUserStatusOperationValue>

const UpdateUserVerifiedOperationValue = z.object({
  user_id: UserIdSchema,
  verified: z.boolean(),
}).describe("認証済みフラグ変更操作の詳細")

export type UpdateUserVerifiedOperationValue = z.infer<typeof UpdateUserVerifiedOperationValue>

const UpdateUserIconOperationValue = z.object({
  user_id: UserIdSchema,
}).describe("ユーザーアイコン変更操作の詳細")

export type UpdateUserIconOperationValue = z.infer<typeof UpdateUserIconOperationValue>

const DeleteUserIconOperationValue = z.object({
  user_id: UserIdSchema,
}).describe("ユーザーアイコン削除操作の詳細")

export type DeleteUserIconOperationValue = z.infer<typeof DeleteUserIconOperationValue>

const ForceWithdrawalOperationValue = z.object({
  user_id: UserIdSchema,
  description: z.string(),
}).describe("強制退会操作の詳細")

export type ForceWithdrawalOperationValue = z.infer<typeof ForceWithdrawalOperationValue>

const DeleteUserStoryOperationValue = z.object({
  user_id: UserIdSchema,
  story_id: z.string(),
}).describe("ストーリー削除操作の詳細")

export type DeleteUserStoryOperationValue = z.infer<typeof DeleteUserStoryOperationValue>

const BlockUserOperationValue = z.object({
  user_id: UserIdSchema.describe("ブロックするユーザー（block-users コレクションを保持するユーザー）"),
  target_user_id: UserIdSchema.describe("ブロック対象のユーザー"),
}).describe("ユーザーブロック追加操作の詳細")

export type BlockUserOperationValue = z.infer<typeof BlockUserOperationValue>

const UnblockUserOperationValue = z.object({
  user_id: UserIdSchema.describe("ブロックを解除するユーザー（block-users コレクションを保持するユーザー）"),
  target_user_id: UserIdSchema.describe("ブロック解除対象のユーザー"),
}).describe("ユーザーブロック解除操作の詳細")

export type UnblockUserOperationValue = z.infer<typeof UnblockUserOperationValue>

// 管理者によるサブスクリプション付与

const GrantUserSubscriptionByAdminOperationValue = z.object({
  subscription_kind: z.enum(["normal", "premium", "premium-plus"]).describe("付与（または解除）するサブスクリプション種別。normal は解除を意味する。"),
}).describe("管理者によるサブスクリプション付与/解除操作の詳細")

export type GrantUserSubscriptionByAdminOperationValue = z.infer<typeof GrantUserSubscriptionByAdminOperationValue>


// デフォルトホロスーツ

const CreateInitialLookOperationValue = z.object({
  item_id: z.string(),
  priority: z.number(),
  status: z.string(),
}).describe("デフォルトホロスーツ作成操作の詳細")

export type CreateInitialLookOperationValue = z.infer<typeof CreateInitialLookOperationValue>

const UpdateInitialLookOperationValue = z.object({
  item_id: z.string(),
  priority: z.number().optional(),
  status: z.string().optional(),
  update_selection_image: z.boolean().optional(),
}).describe("デフォルトホロスーツ更新操作の詳細")

export type UpdateInitialLookOperationValue = z.infer<typeof UpdateInitialLookOperationValue>

const DeleteInitialLookOperationValue = z.object({
  item_id: z.string(),
}).describe("デフォルトホロスーツ削除操作の詳細")

export type DeleteInitialLookOperationValue = z.infer<typeof DeleteInitialLookOperationValue>

// ホームバナー
const CreateHomeBannerOperationValue = z.object({
  home_banner_id: z.string().describe("HomeBannerId"),
  title: z.string().describe("バナータイトル"),
  uri: z.string().describe("リンク先URI"),
  priority: z.number().describe("優先度"),
  status: z.string().describe("ステータス"),
}).describe("ホームバナー作成操作の詳細")

export type CreateHomeBannerOperationValue = z.infer<typeof CreateHomeBannerOperationValue>

const UpdateHomeBannerOperationValue = z.object({
  home_banner_id: z.string().describe("HomeBannerId"),
  title: z.string().optional().describe("バナータイトル"),
  uri: z.string().optional().describe("リンク先URI"),
  priority: z.number().optional().describe("優先度"),
  status: z.string().optional().describe("ステータス"),
}).describe("ホームバナー更新操作の詳細")

export type UpdateHomeBannerOperationValue = z.infer<typeof UpdateHomeBannerOperationValue>

const DeleteHomeBannerOperationValue = z.object({
  home_banner_id: z.string().describe("HomeBannerId"),
}).describe("ホームバナー削除操作の詳細")

export type DeleteHomeBannerOperationValue = z.infer<typeof DeleteHomeBannerOperationValue>

// ホームピックアップ
const CreateHomePickupOperationValue = z.object({
  home_pickup_id: z.string().describe("HomePickupId"),
  kind: z.string().describe("ピックアップ種別"),
  space_id: z.string().optional().describe("SpaceKey"),
}).describe("ホームピックアップ作成操作の詳細")

export type CreateHomePickupOperationValue = z.infer<typeof CreateHomePickupOperationValue>

const DeleteHomePickupOperationValue = z.object({
  home_pickup_id: z.string().describe("HomePickupId"),
}).describe("ホームピックアップ削除操作の詳細")

export type DeleteHomePickupOperationValue = z.infer<typeof DeleteHomePickupOperationValue>

// ホームライブピン
const CreateHomeLivePinOperationValue = z.object({
  home_live_pin_id: z.string().describe("HomeLivePinId"),
  space_key: z.string().describe("SpaceKey"),
  priority: z.number().describe("優先度"),
  status: z.string().describe("ステータス"),
}).describe("ホームライブピン作成操作の詳細")

export type CreateHomeLivePinOperationValue = z.infer<typeof CreateHomeLivePinOperationValue>

const UpdateHomeLivePinOperationValue = z.object({
  home_live_pin_id: z.string().describe("HomeLivePinId"),
  space_key: z.string().optional().describe("SpaceKey"),
  priority: z.number().optional().describe("優先度"),
  status: z.string().optional().describe("ステータス"),
}).describe("ホームライブピン更新操作の詳細")

export type UpdateHomeLivePinOperationValue = z.infer<typeof UpdateHomeLivePinOperationValue>

const DeleteHomeLivePinOperationValue = z.object({
  home_live_pin_id: z.string().describe("HomeLivePinId"),
}).describe("ホームライブピン削除操作の詳細")

export type DeleteHomeLivePinOperationValue = z.infer<typeof DeleteHomeLivePinOperationValue>

// 画像管理

const CreateManagedImageOperationValue = z.object({
  managed_image_id: z.string(),
  name: z.string().optional(),
  usage_types: z.array(z.string()),
}).describe("画像作成操作の詳細")

export type CreateManagedImageOperationValue = z.infer<typeof CreateManagedImageOperationValue>

const ReplaceManagedImageOperationValue = z.object({
  managed_image_id: z.string(),
}).describe("画像差し替え操作の詳細")

export type ReplaceManagedImageOperationValue = z.infer<typeof ReplaceManagedImageOperationValue>

const DeleteManagedImageOperationValue = z.object({
  managed_image_id: z.string(),
}).describe("画像削除操作の詳細")

export type DeleteManagedImageOperationValue = z.infer<typeof DeleteManagedImageOperationValue>

const UpdateManagedImageUsageTypesOperationValue = z.object({
  managed_image_id: z.string(),
  usage_types: z.array(z.string()),
}).describe("画像用途変更操作の詳細")

export type UpdateManagedImageUsageTypesOperationValue = z.infer<typeof UpdateManagedImageUsageTypesOperationValue>

// 抽選のお題

const CreateTopicSuggestionOperationValue = z.object({
  topic_suggestion_id: z.string(),
  kind: z.string(),
  text: z.string(),
  adoption_ratio: z.number(),
}).describe("抽選のお題作成操作の詳細")

export type CreateTopicSuggestionOperationValue = z.infer<typeof CreateTopicSuggestionOperationValue>

const UpdateTopicSuggestionOperationValue = z.object({
  topic_suggestion_id: z.string(),
  kind: z.string(),
  text: z.string(),
  adoption_ratio: z.number(),
  status: z.string(),
}).describe("抽選のお題更新操作の詳細")

export type UpdateTopicSuggestionOperationValue = z.infer<typeof UpdateTopicSuggestionOperationValue>

const DeleteTopicSuggestionOperationValue = z.object({
  topic_suggestion_id: z.string(),
}).describe("抽選のお題削除操作の詳細")

export type DeleteTopicSuggestionOperationValue = z.infer<typeof DeleteTopicSuggestionOperationValue>

// NGワード

const CreateGlobalNgWordOperationValue = z.object({
  global_ng_word_id: z.string(),
  word: z.string(),
  is_regex: z.boolean(),
}).describe("NGワード作成操作の詳細")

export type CreateGlobalNgWordOperationValue = z.infer<typeof CreateGlobalNgWordOperationValue>

const UpdateGlobalNgWordOperationValue = z.object({
  global_ng_word_id: z.string(),
  word: z.string(),
  is_regex: z.boolean(),
}).describe("NGワード更新操作の詳細")

export type UpdateGlobalNgWordOperationValue = z.infer<typeof UpdateGlobalNgWordOperationValue>

const DeleteGlobalNgWordOperationValue = z.object({
  global_ng_word_id: z.string(),
}).describe("NGワード削除操作の詳細")

export type DeleteGlobalNgWordOperationValue = z.infer<typeof DeleteGlobalNgWordOperationValue>

// パワー管理

const CreatePowerOperationValue = z.object({
  power_id: z.string(),
  name: z.string(),
  price: z.number(),
  impact_value: z.number(),
  status: z.string(),
}).describe("パワー作成操作の詳細")

export type CreatePowerOperationValue = z.infer<typeof CreatePowerOperationValue>

const UpdatePowerOperationValue = z.object({
  power_id: z.string(),
  name: z.string().optional(),
  price: z.number().optional(),
  impact_value: z.number().optional(),
  status: z.string().optional(),
  update_icon: z.boolean().optional(),
  update_effect: z.boolean().optional(),
}).describe("パワー更新操作の詳細")

export type UpdatePowerOperationValue = z.infer<typeof UpdatePowerOperationValue>

const DeletePowerOperationValue = z.object({
  power_id: z.string(),
}).describe("パワー削除操作の詳細")

export type DeletePowerOperationValue = z.infer<typeof DeletePowerOperationValue>

// フォローしませんかユーザー

const CreateRecommendedUserOperationValue = z.object({
  user_id: z.string(),
}).describe("フォローしませんかユーザー追加操作の詳細")

export type CreateRecommendedUserOperationValue = z.infer<typeof CreateRecommendedUserOperationValue>

const DeleteRecommendedUserOperationValue = z.object({
  user_id: z.string(),
}).describe("フォローしませんかユーザー削除操作の詳細")

export type DeleteRecommendedUserOperationValue = z.infer<typeof DeleteRecommendedUserOperationValue>

// 許可ドメイン

const CreateAllowedDomainOperationValue = z.object({
  allowed_domain_id: z.string(),
  domain: z.string(),
  is_regex: z.boolean(),
}).describe("許可ドメイン作成操作の詳細")

export type CreateAllowedDomainOperationValue = z.infer<typeof CreateAllowedDomainOperationValue>

const DeleteAllowedDomainOperationValue = z.object({
  allowed_domain_id: z.string(),
}).describe("許可ドメイン削除操作の詳細")

export type DeleteAllowedDomainOperationValue = z.infer<typeof DeleteAllowedDomainOperationValue>

// 一括配信

const CreateBulkDeliveryMasterOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
  delivery_kind: z.enum(BulkDeliveryMasterKind),
}).describe("配信マスタ作成操作の詳細")

export type CreateBulkDeliveryMasterOperationValue = z.infer<typeof CreateBulkDeliveryMasterOperationValue>

const UpdateBulkDeliveryMasterStatusOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
  before_status: z.enum(BulkDeliveryMasterStatus),
  after_status: z.enum(BulkDeliveryMasterStatus),
}).describe("配信マスタステータス変更操作の詳細")

export type UpdateBulkDeliveryMasterStatusOperationValue = z.infer<typeof UpdateBulkDeliveryMasterStatusOperationValue>

const UpdateBulkDeliveryMasterContentOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
}).describe("配信マスタ内容更新操作の詳細")

export type UpdateBulkDeliveryMasterContentOperationValue = z.infer<typeof UpdateBulkDeliveryMasterContentOperationValue>

const UpdateBulkDeliveryMasterDeletionOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
  deletion_start_at: z.number().nullable(),
}).describe("配信マスタ削除時間設定操作の詳細")

export type UpdateBulkDeliveryMasterDeletionOperationValue = z.infer<typeof UpdateBulkDeliveryMasterDeletionOperationValue>

const UpdateBulkDeliveryMasterTargetSettingOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
}).describe("配信マスタ対象設定更新操作の詳細")

export type UpdateBulkDeliveryMasterTargetSettingOperationValue = z.infer<typeof UpdateBulkDeliveryMasterTargetSettingOperationValue>

const DeleteBulkDeliveryMasterOperationValue = z.object({
  bulk_delivery_master_id: z.string(),
}).describe("配信マスタ削除操作の詳細")

export type DeleteBulkDeliveryMasterOperationValue = z.infer<typeof DeleteBulkDeliveryMasterOperationValue>

// 新規ユーザー配布マスタ

const WelcomeDeliveryMasterAuditContent = WelcomeDeliveryMasterMeta
  .omit({ origin_bulk_delivery_id: true })
  .partial()
  .describe("新規ユーザー配布マスタの設定値。create では全フィールド、update では送信されたフィールドのみが含まれる。")

export type WelcomeDeliveryMasterAuditContent = z.infer<typeof WelcomeDeliveryMasterAuditContent>

const CreateWelcomeDeliveryMasterOperationValue = z.object({
  welcome_delivery_master_id: z.string().describe("作成されたWelcomeDeliveryMasterId"),
  content: WelcomeDeliveryMasterAuditContent.describe("作成された配布マスタの全設定値"),
}).describe("新規ユーザー配布マスタ作成操作の詳細")

export type CreateWelcomeDeliveryMasterOperationValue = z.infer<typeof CreateWelcomeDeliveryMasterOperationValue>

const UpdateWelcomeDeliveryMasterOperationValue = z.object({
  welcome_delivery_master_id: z.string().describe("WelcomeDeliveryMasterId"),
  changes: WelcomeDeliveryMasterAuditContent.describe("更新されたフィールドのみ"),
}).describe("新規ユーザー配布マスタ更新操作の詳細")

export type UpdateWelcomeDeliveryMasterOperationValue = z.infer<typeof UpdateWelcomeDeliveryMasterOperationValue>

// お知らせ

const CreateSystemNotificationOperationValue = z.object({
  system_notification_id: z.string(),
  title: z.string(),
}).describe("お知らせ作成操作の詳細")

export type CreateSystemNotificationOperationValue = z.infer<typeof CreateSystemNotificationOperationValue>

const UpdateSystemNotificationOperationValue = z.object({
  system_notification_id: z.string(),
  title: z.string(),
}).describe("お知らせ更新操作の詳細")

export type UpdateSystemNotificationOperationValue = z.infer<typeof UpdateSystemNotificationOperationValue>

const DeleteSystemNotificationOperationValue = z.object({
  system_notification_id: z.string(),
}).describe("お知らせ削除操作の詳細")

export type DeleteSystemNotificationOperationValue = z.infer<typeof DeleteSystemNotificationOperationValue>

// ショップバナー

const CreateShopBannerOperationValue = z.object({
  shop_banner_id: z.string().describe("ShopBannerId"),
  uri: z.string().describe("リンク先URI"),
  priority: z.number().describe("優先度"),
  status: z.string().describe("ステータス"),
}).describe("ショップバナー作成操作の詳細")

export type CreateShopBannerOperationValue = z.infer<typeof CreateShopBannerOperationValue>

const UpdateShopBannerOperationValue = z.object({
  shop_banner_id: z.string().describe("ShopBannerId"),
  uri: z.string().optional().describe("リンク先URI"),
  priority: z.number().optional().describe("優先度"),
  status: z.string().optional().describe("ステータス"),
}).describe("ショップバナー更新操作の詳細")

export type UpdateShopBannerOperationValue = z.infer<typeof UpdateShopBannerOperationValue>

const DeleteShopBannerOperationValue = z.object({
  shop_banner_id: z.string().describe("ShopBannerId"),
}).describe("ショップバナー削除操作の詳細")

export type DeleteShopBannerOperationValue = z.infer<typeof DeleteShopBannerOperationValue>

// ショップカスタムタグ

const CreateShopCustomTagOperationValue = z.object({
  shop_custom_tag_id: z.string().describe("ShopCustomTagId"),
  title: z.string().describe("タグタイトル"),
  kind: z.string().describe("タグ種別"),
  priority: z.number().describe("優先度"),
  status: z.string().describe("ステータス"),
}).describe("ショップカスタムタグ作成操作の詳細")

export type CreateShopCustomTagOperationValue = z.infer<typeof CreateShopCustomTagOperationValue>

const UpdateShopCustomTagOperationValue = z.object({
  shop_custom_tag_id: z.string().describe("ShopCustomTagId"),
  title: z.string().optional().describe("タグタイトル"),
  priority: z.number().optional().describe("優先度"),
  status: z.string().optional().describe("ステータス"),
}).describe("ショップカスタムタグ更新操作の詳細")

export type UpdateShopCustomTagOperationValue = z.infer<typeof UpdateShopCustomTagOperationValue>

const DeleteShopCustomTagOperationValue = z.object({
  shop_custom_tag_id: z.string().describe("ShopCustomTagId"),
}).describe("ショップカスタムタグ削除操作の詳細")

export type DeleteShopCustomTagOperationValue = z.infer<typeof DeleteShopCustomTagOperationValue>

// ショップTOP編成

const UpdateShopTopCompositionFirstOperationValue = z.object({
  item_count: z.number().describe("編成アイテム数"),
}).describe("ショップTOP編成（上部）更新操作の詳細")

export type UpdateShopTopCompositionFirstOperationValue = z.infer<typeof UpdateShopTopCompositionFirstOperationValue>

const UpdateShopTopCompositionListOperationValue = z.object({
  item_count: z.number().describe("編成アイテム数"),
}).describe("ショップTOP編成（リスト）更新操作の詳細")

export type UpdateShopTopCompositionListOperationValue = z.infer<typeof UpdateShopTopCompositionListOperationValue>

const CreateGeoIpCidrOverrideOperationValue = z.object({
  cidr: z.string().describe("CIDR表記"),
  ip_version: z.string().describe("IPバージョン（v4/v6）"),
  action: z.string().describe("アクション（include/exclude）"),
  note: z.string().describe("変更理由のメモ"),
}).describe("GeoIP CIDRオーバーライド追加操作の詳細")

export type CreateGeoIpCidrOverrideOperationValue = z.infer<typeof CreateGeoIpCidrOverrideOperationValue>

const DeleteGeoIpCidrOverrideOperationValue = z.object({
  cidr: z.string().describe("CIDR表記"),
  ip_version: z.string().describe("IPバージョン（v4/v6）"),
  action: z.string().describe("アクション（include/exclude）"),
}).describe("GeoIP CIDRオーバーライド削除操作の詳細")

export type DeleteGeoIpCidrOverrideOperationValue = z.infer<typeof DeleteGeoIpCidrOverrideOperationValue>

// 管理者
const RegisterAdminOperationValue = z.object({
  admin_id: z.string().describe("登録された管理者のID"),
  email: z.string().describe("登録時のメールアドレス"),
}).describe("管理者登録の操作値")

export type RegisterAdminOperationValue = z.infer<typeof RegisterAdminOperationValue>

const UpdateAdminOperationValue = z.object({
  admin_id: z.string().describe("更新された管理者のID"),
  permission: z.unknown().describe("変更後の権限設定"),
}).describe("管理者更新の操作値")

export type UpdateAdminOperationValue = z.infer<typeof UpdateAdminOperationValue>

const DeleteAdminOperationValue = z.object({
  admin_id: z.string().describe("削除された管理者のID"),
}).describe("管理者削除の操作値")

export type DeleteAdminOperationValue = z.infer<typeof DeleteAdminOperationValue>

const InviteAdminOperationValue = z.object({
  email: z.string().describe("招待先メールアドレス"),
  permission: z.unknown().describe("招待時の権限設定"),
}).describe("管理者招待の操作値")

export type InviteAdminOperationValue = z.infer<typeof InviteAdminOperationValue>

const DeleteAdminInviteOperationValue = z.object({
  admin_invite_id: z.string().describe("削除された招待のID"),
}).describe("管理者招待削除の操作値")

export type DeleteAdminInviteOperationValue = z.infer<typeof DeleteAdminInviteOperationValue>

// アプリ設定

const SetAppConfigRequirementOperationValue = z.object({
  config_id: z.string().describe("設定ドキュメントID"),
  ios_min_version: z.string().describe("iOS最低バージョン"),
  android_min_version: z.string().describe("Android最低バージョン"),
}).describe("強制アップデート設定操作の詳細")

export type SetAppConfigRequirementOperationValue = z.infer<typeof SetAppConfigRequirementOperationValue>

const DeleteAppConfigRequirementOperationValue = z.object({
  config_id: z.string().describe("削除された設定ドキュメントID"),
}).describe("強制アップデート設定削除操作の詳細")

export type DeleteAppConfigRequirementOperationValue = z.infer<typeof DeleteAppConfigRequirementOperationValue>

const SetAppConfigTermsAgreementOperationValue = z.object({
  config_id: z.string().describe("設定ドキュメントID"),
  kind: z.string().describe("規約種別（terms-of-service / privacy）"),
  version: z.string().describe("規約バージョン"),
  effective_at: z.number().describe("有効開始日時"),
}).describe("利用規約設定操作の詳細")

export type SetAppConfigTermsAgreementOperationValue = z.infer<typeof SetAppConfigTermsAgreementOperationValue>

const SetAppConfigGlobalNavigationOperationValue = z.object({
  config_id: z.string().describe("設定ドキュメントID"),
  shop_unread_at: z.number().nullable().describe("ショップ未読フラグ更新日時（null=バッジ無効）"),
}).describe("グローバルナビゲーション設定操作の詳細")

export type SetAppConfigGlobalNavigationOperationValue = z.infer<typeof SetAppConfigGlobalNavigationOperationValue>

// 変換レート管理（パワー→LIVEポイント / LIVEポイント→無償コイン / 無償パワー→期限付き無償コイン 統合）

const SetConversionRateOperationValue = z.object({
  config_id: z.string().describe("設定ドキュメントID"),
  updated_sections: z.array(z.string()).describe("更新されたセクション（from_power / to_free_coin / to_expiring_free_coin）"),
}).describe("変換レート設定操作の詳細")

export type SetConversionRateOperationValue = z.infer<typeof SetConversionRateOperationValue>

// ユーザー個別レート管理

const SetUserConversionRateOperationValue = z.object({
  section: z.string().describe("対象セクション（from_power / to_free_coin）"),
  conversion_rate: z.number().int().describe("設定された変換レートの分子"),
  conversion_rate_unit: z.number().int().describe("変換レートの分母（固定値 10000）"),
  effective_from: z.number().describe("有効開始日時 (UNIX Timestamp millis)"),
}).describe("ユーザー個別変換レート設定操作の詳細")

export type SetUserConversionRateOperationValue = z.infer<typeof SetUserConversionRateOperationValue>

const DeleteUserConversionRateOperationValue = z.object({
  section: z.string().describe("対象セクション（from_power / to_free_coin）"),
  previous_conversion_rate: z.number().int().optional().describe("削除前の変換レートの分子"),
}).describe("ユーザー個別変換レート削除操作の詳細")

export type DeleteUserConversionRateOperationValue = z.infer<typeof DeleteUserConversionRateOperationValue>

// LIVEポイント加減算

const UpdateLivePointsOperationValue = z.object({
  amount: z.number().int().describe("変動量（正=加算、負=減算）"),
  reason: z.string().describe("調整理由"),
  balance_after: z.number().int().describe("調整後の残高"),
}).describe("LIVEポイント加減算操作の詳細")

export type UpdateLivePointsOperationValue = z.infer<typeof UpdateLivePointsOperationValue>

// LIVEポイント交換停止/解除

const UpdateLivePointExchangeBlockOperationValue = z.object({
  blocked: z.boolean().describe("true=停止、false=解除"),
  reason: z.string().describe("停止/解除理由"),
}).describe("LIVEポイント交換停止/解除操作の詳細")

export type UpdateLivePointExchangeBlockOperationValue = z.infer<typeof UpdateLivePointExchangeBlockOperationValue>

// LIVEポイント変換ブロック（LIVE単位）

const UpdateLivePointConversionBlockOperationValue = z.object({
  live_id: z.string().describe("対象のLiveId"),
  blocked: z.boolean().describe("true=ブロック、false=解除"),
  reason: z.string().describe("ブロック/解除理由"),
}).describe("LIVEポイント変換ブロック（LIVE単位）操作の詳細")

export type UpdateLivePointConversionBlockOperationValue = z.infer<typeof UpdateLivePointConversionBlockOperationValue>

// メンテナンス管理

const CreateMaintenanceScheduleOperationValue = z.object({
  maintenance_schedule_id: z.string().describe("作成されたスケジュールのID"),
  title: z.string().describe("タイトル"),
  area: z.unknown().describe("対象エリア"),
  status: z.string().describe("ステータス"),
}).describe("メンテナンススケジュール作成操作の詳細")

export type CreateMaintenanceScheduleOperationValue = z.infer<typeof CreateMaintenanceScheduleOperationValue>

const UpdateMaintenanceScheduleOperationValue = z.object({
  maintenance_schedule_id: z.string().describe("更新されたスケジュールのID"),
  title: z.string().describe("タイトル"),
  area: z.unknown().describe("エリア"),
  stage: z.string().describe("ステージ"),
  status: z.string().describe("ステータス"),
}).describe("メンテナンススケジュール更新操作の詳細")

export type UpdateMaintenanceScheduleOperationValue = z.infer<typeof UpdateMaintenanceScheduleOperationValue>

const DeleteMaintenanceScheduleOperationValue = z.object({
  maintenance_schedule_id: z.string().describe("削除されたスケジュールのID"),
}).describe("メンテナンススケジュール削除操作の詳細")

export type DeleteMaintenanceScheduleOperationValue = z.infer<typeof DeleteMaintenanceScheduleOperationValue>

const DuplicateMaintenanceScheduleOperationValue = z.object({
  source_maintenance_schedule_id: z.string().describe("複製元のスケジュールID"),
  new_maintenance_schedule_id: z.string().describe("複製先のスケジュールID"),
  title: z.string().describe("タイトル"),
}).describe("メンテナンススケジュール複製操作の詳細")

export type DuplicateMaintenanceScheduleOperationValue = z.infer<typeof DuplicateMaintenanceScheduleOperationValue>

const CreateMaintenanceInternalUserOperationValue = z.object({
  user_id: z.string().describe("追加されたユーザーID"),
  note: z.string().describe("メモ"),
}).describe("内部確認ユーザー追加操作の詳細")

export type CreateMaintenanceInternalUserOperationValue = z.infer<typeof CreateMaintenanceInternalUserOperationValue>

const UpdateMaintenanceInternalUserOperationValue = z.object({
  user_id: z.string().describe("更新されたユーザーID"),
  note: z.string().describe("メモ"),
}).describe("内部確認ユーザー更新操作の詳細")

export type UpdateMaintenanceInternalUserOperationValue = z.infer<typeof UpdateMaintenanceInternalUserOperationValue>

const DeleteMaintenanceInternalUserOperationValue = z.object({
  user_id: z.string().describe("削除されたユーザーID"),
}).describe("内部確認ユーザー削除操作の詳細")

export type DeleteMaintenanceInternalUserOperationValue = z.infer<typeof DeleteMaintenanceInternalUserOperationValue>

// ライブコメント管理

const DeleteLiveCommentOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  live_id: z.string().describe("LiveId"),
  comment_id: z.string().describe("LiveCommentId"),
  comment_kind: z.string().describe("コメント種別（image, text, emotion 等）"),
}).describe("ライブコメント削除操作の詳細")

export type DeleteLiveCommentOperationValue = z.infer<typeof DeleteLiveCommentOperationValue>

// お題投稿（抽選参加者）管理

const DeleteSelectionParticipantOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  live_id: z.string().describe("LiveId"),
  selection_id: z.string().describe("SelectionId"),
  participant_id: z.string().describe("SelectionParticipantId"),
}).describe("お題投稿（抽選参加者）削除操作の詳細")

export type DeleteSelectionParticipantOperationValue = z.infer<typeof DeleteSelectionParticipantOperationValue>

// システム運用 / 運用補助

const UpdateSpaceThumbnailSettingOperationValue = z.object({
  changes: z.object({
    generator_look_image_count: z.number().int().optional().describe("ホロスーツ画像数"),
    generator_space_update_interval_ms: z.number().int().optional().describe("スペース更新間隔(ms)"),
    generator_live_update_interval_ms: z.number().int().optional().describe("ライブ更新間隔(ms)"),
    generator_space_retry_interval_ms: z.number().int().optional().describe("スペースリトライ間隔(ms)"),
    generator_live_retry_interval_ms: z.number().int().optional().describe("ライブリトライ間隔(ms)"),
    generator_max_concurrency: z.number().int().optional().describe("最大同時実行数"),
    home_overflow_height_power_duration_ms: z.number().int().optional().describe("ホーム overflow パワー期間(ms)"),
    home_overflow_height_total_price_duration_ms: z.number().int().optional().describe("ホーム overflow 合計価格期間(ms)"),
    home_overflow_height_total_price_threshold: z.number().optional().describe("ホーム overflow 合計価格しきい値"),
  }).describe("変更されたフィールドのみ"),
}).describe("スペースサムネイル設定更新操作の詳細")

export type UpdateSpaceThumbnailSettingOperationValue = z.infer<typeof UpdateSpaceThumbnailSettingOperationValue>

const UpdateVrmAccountImageSettingOperationValue = z.object({
  changes: z.object({
    kind: z.string().optional().describe("撮影時の表情"),
    quality: z.number().int().optional().describe("WebP品質 (0-100)"),
    margin_left: z.number().int().optional().describe("左マージン"),
    margin_right: z.number().int().optional().describe("右マージン"),
    margin_top: z.number().int().optional().describe("上マージン"),
    margin_bottom: z.number().int().optional().describe("下マージン"),
    layout: z.string().optional().describe("カスタムレイアウト"),
  }).describe("変更されたフィールドのみ"),
}).describe("VRMアカウント画像設定更新操作の詳細")

export type UpdateVrmAccountImageSettingOperationValue = z.infer<typeof UpdateVrmAccountImageSettingOperationValue>

const RequestToGenerateActiveSpaceThumbnailsOperationValue = z.object({
  space_key: z.string().describe("対象スペースのキー"),
}).describe("アクティブスペースのサムネイル生成要求の詳細")

export type RequestToGenerateActiveSpaceThumbnailsOperationValue = z.infer<typeof RequestToGenerateActiveSpaceThumbnailsOperationValue>

const RequestToGenerateLiveThumbnailsOperationValue = z.object({
  space_key: z.string().describe("対象スペースのキー"),
  live_id: z.string().describe("対象ライブのID"),
}).describe("ライブサムネイル生成要求の詳細")

export type RequestToGenerateLiveThumbnailsOperationValue = z.infer<typeof RequestToGenerateLiveThumbnailsOperationValue>

const SendDebugPushOperationValue = z.object({
  user_ids: z.array(UserIdSchema).describe("対象ユーザーIDの配列"),
}).describe("デバッグプッシュ送信操作の詳細")

export type SendDebugPushOperationValue = z.infer<typeof SendDebugPushOperationValue>

const SyncCoinBalanceCacheOperationValue = z.object({
  user_id: UserIdSchema.describe("対象ユーザーID"),
}).describe("コイン残高キャッシュ再同期操作の詳細")

export type SyncCoinBalanceCacheOperationValue = z.infer<typeof SyncCoinBalanceCacheOperationValue>

const RecoverAlgoliaOperationValue = z.object({
  target: z.enum(["users", "items", "lives", "all"]).describe("リカバリ対象"),
  direction: z.enum(["create", "delete", "both"]).describe("リカバリ方向"),
}).describe("Algolia リカバリタスク作成操作の詳細")

export type RecoverAlgoliaOperationValue = z.infer<typeof RecoverAlgoliaOperationValue>


// コイン管理

const InsertCoinPackOperationValue = z.object({
  coin_pack_id: z.string().describe("CoinPackId"),
  store: z.number().describe("PlatformStore"),
  app_domain: z.string().describe("アプリドメイン"),
  product_id: z.string().describe("ストア商品ID"),
}).describe("コインパック追加操作の詳細")

export type InsertCoinPackOperationValue = z.infer<typeof InsertCoinPackOperationValue>

const RevertCoinPackOperationValue = z.object({
  coin_pack_id: z.string().describe("CoinPackId"),
  store: z.number().describe("PlatformStore"),
  app_domain: z.string().describe("アプリドメイン"),
}).describe("コインパック再公開操作の詳細")

export type RevertCoinPackOperationValue = z.infer<typeof RevertCoinPackOperationValue>

const RemoveCoinPackOperationValue = z.object({
  coin_pack_id: z.string().describe("CoinPackId"),
  store: z.number().describe("PlatformStore"),
  app_domain: z.string().describe("アプリドメイン"),
}).describe("コインパック削除操作の詳細")

export type RemoveCoinPackOperationValue = z.infer<typeof RemoveCoinPackOperationValue>

const UpdateCoinPackOperationValue = z.object({
  coin_pack_id: z.string().describe("CoinPackId"),
  store: z.number().describe("PlatformStore"),
  app_domain: z.string().describe("アプリドメイン"),
}).describe("コインパック更新操作の詳細")

export type UpdateCoinPackOperationValue = z.infer<typeof UpdateCoinPackOperationValue>

// サブスクリプション

const CreateSubscriptionPlanOperationValue = z.object({
  subscription_plan_id: z.string().describe("SubscriptionPlanId"),
  subscription_kind: z.string().describe("サブスクリプション種別"),
}).describe("サブスクリプションプラン作成操作の詳細")

export type CreateSubscriptionPlanOperationValue = z.infer<typeof CreateSubscriptionPlanOperationValue>

const UpdateSubscriptionPlanOperationValue = z.object({
  subscription_plan_id: z.string().describe("SubscriptionPlanId"),
  status: z.string().optional().describe("変更後のステータス"),
}).describe("サブスクリプションプラン更新操作の詳細")

export type UpdateSubscriptionPlanOperationValue = z.infer<typeof UpdateSubscriptionPlanOperationValue>

const DeleteSubscriptionPlanOperationValue = z.object({
  subscription_plan_id: z.string().describe("SubscriptionPlanId"),
}).describe("サブスクリプションプラン削除操作の詳細")

export type DeleteSubscriptionPlanOperationValue = z.infer<typeof DeleteSubscriptionPlanOperationValue>

const CreateSubscriptionProductOperationValue = z.object({
  subscription_product_id: z.string().describe("SubscriptionProductId"),
  plan_id: z.string().describe("SubscriptionPlanId"),
  product_id: z.string().describe("ストア商品ID"),
  store: z.number().describe("PlatformStore"),
  price: z.number().describe("価格"),
}).describe("サブスクリプション商品作成操作の詳細")

export type CreateSubscriptionProductOperationValue = z.infer<typeof CreateSubscriptionProductOperationValue>

const UpdateSubscriptionProductOperationValue = z.object({
  subscription_product_id: z.string().describe("SubscriptionProductId"),
  plan_id: z.string().describe("SubscriptionPlanId"),
}).describe("サブスクリプション商品更新操作の詳細")

export type UpdateSubscriptionProductOperationValue = z.infer<typeof UpdateSubscriptionProductOperationValue>

const DeleteSubscriptionProductOperationValue = z.object({
  subscription_product_id: z.string().describe("SubscriptionProductId"),
  plan_id: z.string().describe("SubscriptionPlanId"),
}).describe("サブスクリプション商品削除操作の詳細")

export type DeleteSubscriptionProductOperationValue = z.infer<typeof DeleteSubscriptionProductOperationValue>

const CancelIABSubscriptionsOperationValue = z.object({
  user_ids: z.array(z.string()).describe("キャンセル対象のユーザーID一覧"),
  results: z.array(z.object({
    user_id: z.string(),
    status: z.enum(["success", "error"]),
    error: z.string().optional(),
  })).describe("各ユーザーのキャンセル結果"),
}).describe("Android サブスクリプション一括キャンセル操作の詳細")

export type CancelIABSubscriptionsOperationValue = z.infer<typeof CancelIABSubscriptionsOperationValue>

// サブスクリプションアイテム付与マスタ

const CreateSubscriptionItemGrantMasterOperationValue = z.object({
  subscription_item_grant_master_id: z.string().describe("SubscriptionItemGrantMasterId"),
  item_ids: z.array(z.string()).describe("付与アイテムID一覧"),
}).describe("サブスクリプションアイテム付与マスタ作成操作の詳細")

export type CreateSubscriptionItemGrantMasterOperationValue = z.infer<typeof CreateSubscriptionItemGrantMasterOperationValue>

const UpdateSubscriptionItemGrantMasterOperationValue = z.object({
  subscription_item_grant_master_id: z.string().describe("SubscriptionItemGrantMasterId"),
}).describe("サブスクリプションアイテム付与マスタ更新操作の詳細")

export type UpdateSubscriptionItemGrantMasterOperationValue = z.infer<typeof UpdateSubscriptionItemGrantMasterOperationValue>

const DeleteSubscriptionItemGrantMasterOperationValue = z.object({
  subscription_item_grant_master_id: z.string().describe("SubscriptionItemGrantMasterId"),
}).describe("サブスクリプションアイテム付与マスタ削除操作の詳細")

export type DeleteSubscriptionItemGrantMasterOperationValue = z.infer<typeof DeleteSubscriptionItemGrantMasterOperationValue>

// ブランド管理

const CreateBrandOperationValue = z.object({
  brand_id: z.string().describe("作成されたBrandId"),
  name: z.string().describe("ブランド名"),
}).describe("ブランド作成操作の詳細")

export type CreateBrandOperationValue = z.infer<typeof CreateBrandOperationValue>

const UpdateBrandOperationValue = z.object({
  brand_id: z.string().describe("BrandId"),
  name: z.string().optional().describe("変更後のブランド名"),
  status: z.string().optional().describe("変更後のステータス"),
}).describe("ブランド更新操作の詳細")

export type UpdateBrandOperationValue = z.infer<typeof UpdateBrandOperationValue>

const DeleteBrandOperationValue = z.object({
  brand_id: z.string().describe("削除されたBrandId"),
}).describe("ブランド削除操作の詳細")

export type DeleteBrandOperationValue = z.infer<typeof DeleteBrandOperationValue>

// アイテム管理

const CreateItemOperationValue = z.object({
  item_id: z.string().describe("作成されたItemId"),
  name: z.string().describe("アイテム名"),
  kind: z.string().describe("アイテム種別 (look / space-background / space-bgm)"),
}).describe("アイテム作成操作の詳細")

export type CreateItemOperationValue = z.infer<typeof CreateItemOperationValue>

const UpdateItemOperationValue = z.object({
  item_id: z.string().describe("ItemId"),
  name: z.string().optional().describe("変更後のアイテム名"),
  status: z.string().optional().describe("変更後のステータス"),
}).describe("アイテム更新操作の詳細")

export type UpdateItemOperationValue = z.infer<typeof UpdateItemOperationValue>

const DeleteItemOperationValue = z.object({
  item_id: z.string().describe("削除されたItemId"),
}).describe("アイテム削除操作の詳細")

export type DeleteItemOperationValue = z.infer<typeof DeleteItemOperationValue>

// アイテム配信（販売期間）

const CreateItemDistributionOperationValue = z.object({
  item_id: z.string().describe("ItemId"),
  item_distribution_id: z.string().describe("作成されたItemDistributionId"),
  start_at: z.number().describe("販売開始日時 (unix ms)"),
  end_at: z.number().nullable().optional().describe("販売終了日時 (unix ms)。null/未指定の場合は無期限。"),
}).describe("アイテム配信（販売期間）作成操作の詳細")

export type CreateItemDistributionOperationValue = z.infer<typeof CreateItemDistributionOperationValue>

const UpdateItemDistributionOperationValue = z.object({
  item_id: z.string().describe("ItemId"),
  item_distribution_id: z.string().describe("ItemDistributionId"),
  start_at: z.number().describe("変更後の販売開始日時 (unix ms)"),
  end_at: z.number().nullable().optional().describe("変更後の販売終了日時 (unix ms)。null/未指定の場合は無期限。"),
}).describe("アイテム配信（販売期間）更新操作の詳細")

export type UpdateItemDistributionOperationValue = z.infer<typeof UpdateItemDistributionOperationValue>

const DeleteItemDistributionOperationValue = z.object({
  item_id: z.string().describe("ItemId"),
  item_distribution_id: z.string().describe("削除されたItemDistributionId"),
}).describe("アイテム配信（販売期間）削除操作の詳細")

export type DeleteItemDistributionOperationValue = z.infer<typeof DeleteItemDistributionOperationValue>

// セール管理

const CreateSaleOperationValue = z.object({
  sale_id: z.string().describe("作成されたSaleId"),
  name: z.string().describe("セール名"),
  kind: z.string().describe("セール種別 (all / item-kind / brand / collection / items)"),
  percentage: z.number().describe("割引率 (パーセント)"),
  start_at: z.number().describe("セール開始日時 (unix ms)"),
  end_at: z.number().nullable().describe("セール終了日時 (unix ms)。null は無期限を意味する。"),
}).describe("セール作成操作の詳細")

export type CreateSaleOperationValue = z.infer<typeof CreateSaleOperationValue>

const UpdateSaleOperationValue = z.object({
  sale_id: z.string().describe("SaleId"),
  name: z.string().describe("変更後のセール名"),
  kind: z.string().describe("変更後のセール種別"),
  percentage: z.number().describe("変更後の割引率 (パーセント)"),
  start_at: z.number().describe("変更後のセール開始日時 (unix ms)"),
  end_at: z.number().nullable().describe("変更後のセール終了日時 (unix ms)。null は無期限を意味する。"),
}).describe("セール更新操作の詳細")

export type UpdateSaleOperationValue = z.infer<typeof UpdateSaleOperationValue>

const DeleteSaleOperationValue = z.object({
  sale_id: z.string().describe("削除されたSaleId"),
}).describe("セール削除操作の詳細")

export type DeleteSaleOperationValue = z.infer<typeof DeleteSaleOperationValue>

// ショップ検索おすすめアイテム

const UpdateShopSearchRecommendedItemsOperationValue = z.object({
  items: z.array(z.object({
    item_id: z.string().describe("ItemId"),
    item_distribution_id: z.string().describe("ItemDistributionId"),
    recommend_priority: z.number().nullable().describe("変更後のおすすめ優先度。null はおすすめから外すことを意味する。"),
  })).describe("更新対象のアイテム一覧"),
}).describe("ショップ検索おすすめアイテム一括更新操作の詳細")

export type UpdateShopSearchRecommendedItemsOperationValue = z.infer<typeof UpdateShopSearchRecommendedItemsOperationValue>

// スペース管理

const CreateSpaceByAdminOperationValue = z.object({
  space_key: z.string().describe("作成されたスペースのSpaceKey"),
  user_id: z.string().describe("オーナーのUserId"),
  name: z.string().describe("スペース名"),
}).describe("管理者によるスペース作成操作の詳細")

export type CreateSpaceByAdminOperationValue = z.infer<typeof CreateSpaceByAdminOperationValue>

const UpdateSpaceByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  name: z.string().describe("変更後のスペース名"),
}).describe("管理者によるスペース更新操作の詳細")

export type UpdateSpaceByAdminOperationValue = z.infer<typeof UpdateSpaceByAdminOperationValue>

const DeleteSpaceByAdminOperationValue = z.object({
  space_key: z.string().describe("削除されたSpaceKey"),
}).describe("管理者によるスペース削除操作の詳細")

export type DeleteSpaceByAdminOperationValue = z.infer<typeof DeleteSpaceByAdminOperationValue>

const AddUserToSpaceByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  user_id: z.string().describe("追加されたUserId"),
}).describe("管理者によるスペースへのユーザー追加操作の詳細")

export type AddUserToSpaceByAdminOperationValue = z.infer<typeof AddUserToSpaceByAdminOperationValue>

const RemoveUserFromSpaceByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  user_id: z.string().describe("削除されたUserId"),
}).describe("管理者によるスペースからのユーザー削除操作の詳細")

export type RemoveUserFromSpaceByAdminOperationValue = z.infer<typeof RemoveUserFromSpaceByAdminOperationValue>

const UpdateSpaceUserRoleByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  user_id: z.string().describe("ロールが変更されたUserId"),
  previous_role: SpaceUserRole.describe("変更前のロール"),
  role: SpaceUserRole.describe("変更後のロール"),
}).describe("管理者によるスペースユーザーのロール変更操作の詳細")

export type UpdateSpaceUserRoleByAdminOperationValue = z.infer<typeof UpdateSpaceUserRoleByAdminOperationValue>

const StopLiveByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  live_id: z.string().describe("停止されたLiveId"),
}).describe("管理者によるライブ停止操作の詳細")

export type StopLiveByAdminOperationValue = z.infer<typeof StopLiveByAdminOperationValue>

const UpdateLiveByAdminOperationValue = z.object({
  space_key: z.string().describe("SpaceKey"),
  live_id: z.string().describe("LiveId"),
  genre_id: z.string().optional().describe("変更後のジャンルID"),
  tags: z.array(z.string()).optional().describe("変更後のタグ"),
  can_enter: z.boolean().optional().describe("入室可否"),
}).describe("管理者によるライブ設定更新操作の詳細")

export type UpdateLiveByAdminOperationValue = z.infer<typeof UpdateLiveByAdminOperationValue>

// スペースの背景

const CreateSpaceBackgroundOperationValue = z.object({
  space_background_id: z.string().describe("SpaceBackgroundId"),
  name: z.string().describe("背景名"),
}).describe("スペースの背景作成操作の詳細")

export type CreateSpaceBackgroundOperationValue = z.infer<typeof CreateSpaceBackgroundOperationValue>

const UpdateSpaceBackgroundOperationValue = z.object({
  space_background_id: z.string().describe("SpaceBackgroundId"),
  name: z.string().optional().describe("変更後の背景名"),
}).describe("スペースの背景更新操作の詳細")

export type UpdateSpaceBackgroundOperationValue = z.infer<typeof UpdateSpaceBackgroundOperationValue>

const DeleteSpaceBackgroundOperationValue = z.object({
  space_background_id: z.string().describe("削除されたSpaceBackgroundId"),
}).describe("スペースの背景削除操作の詳細")

export type DeleteSpaceBackgroundOperationValue = z.infer<typeof DeleteSpaceBackgroundOperationValue>

// スペースのBGM

const CreateSpaceBGMOperationValue = z.object({
  space_bgm_id: z.string().describe("SpaceBGMId"),
  name: z.string().describe("BGM名"),
}).describe("スペースのBGM作成操作の詳細")

export type CreateSpaceBGMOperationValue = z.infer<typeof CreateSpaceBGMOperationValue>

const UpdateSpaceBGMOperationValue = z.object({
  space_bgm_id: z.string().describe("SpaceBGMId"),
  name: z.string().optional().describe("変更後のBGM名"),
}).describe("スペースのBGM更新操作の詳細")

export type UpdateSpaceBGMOperationValue = z.infer<typeof UpdateSpaceBGMOperationValue>

const DeleteSpaceBGMOperationValue = z.object({
  space_bgm_id: z.string().describe("削除されたSpaceBGMId"),
}).describe("スペースのBGM削除操作の詳細")

export type DeleteSpaceBGMOperationValue = z.infer<typeof DeleteSpaceBGMOperationValue>

// スタンプカード

const CreateStampCardOperationValue = z.object({
  stamp_card_id: z.string().describe("StampCardId"),
  name: z.string().describe("カード名"),
  status: z.string().describe("ステータス"),
  priority: z.number().describe("優先度"),
}).describe("スタンプカード作成操作の詳細")

export type CreateStampCardOperationValue = z.infer<typeof CreateStampCardOperationValue>

const UpdateStampCardOperationValue = z.object({
  stamp_card_id: z.string().describe("StampCardId"),
  name: z.string().optional().describe("カード名"),
  status: z.string().optional().describe("ステータス"),
  priority: z.number().optional().describe("優先度"),
}).describe("スタンプカード更新操作の詳細")

export type UpdateStampCardOperationValue = z.infer<typeof UpdateStampCardOperationValue>

const DeleteStampCardOperationValue = z.object({
  stamp_card_id: z.string().describe("StampCardId"),
}).describe("スタンプカード削除操作の詳細")

export type DeleteStampCardOperationValue = z.infer<typeof DeleteStampCardOperationValue>

// スタンプレーン

const CreateStampLaneOperationValue = z.object({
  stamp_lane_id: z.string().describe("StampLaneId"),
  card_id: z.string().describe("StampCardId"),
}).describe("スタンプレーン作成操作の詳細")

export type CreateStampLaneOperationValue = z.infer<typeof CreateStampLaneOperationValue>

const UpdateStampLaneOperationValue = z.object({
  stamp_lane_id: z.string().describe("StampLaneId"),
  card_id: z.string().describe("StampCardId"),
}).describe("スタンプレーン更新操作の詳細")

export type UpdateStampLaneOperationValue = z.infer<typeof UpdateStampLaneOperationValue>

const DeleteStampLaneOperationValue = z.object({
  stamp_lane_id: z.string().describe("StampLaneId"),
  card_id: z.string().describe("StampCardId"),
}).describe("スタンプレーン削除操作の詳細")

export type DeleteStampLaneOperationValue = z.infer<typeof DeleteStampLaneOperationValue>

// スタンプレーン報酬

const CreateStampLaneRewardOperationValue = z.object({
  stamp_lane_reward_id: z.string().describe("StampLaneRewardId"),
  card_id: z.string().describe("StampCardId"),
  lane_id: z.string().describe("StampLaneId"),
  name: z.string().describe("報酬名"),
  kind: z.string().describe("報酬種別（item / coin）"),
  threshold_stamps: z.number().describe("必要スタンプ数"),
}).describe("スタンプレーン報酬作成操作の詳細")

export type CreateStampLaneRewardOperationValue = z.infer<typeof CreateStampLaneRewardOperationValue>

const UpdateStampLaneRewardOperationValue = z.object({
  stamp_lane_reward_id: z.string().describe("StampLaneRewardId"),
  card_id: z.string().describe("StampCardId"),
  lane_id: z.string().describe("StampLaneId"),
  name: z.string().optional().describe("報酬名"),
  kind: z.string().optional().describe("報酬種別（item / coin）"),
  threshold_stamps: z.number().optional().describe("必要スタンプ数"),
}).describe("スタンプレーン報酬更新操作の詳細")

export type UpdateStampLaneRewardOperationValue = z.infer<typeof UpdateStampLaneRewardOperationValue>

const DeleteStampLaneRewardOperationValue = z.object({
  stamp_lane_reward_id: z.string().describe("StampLaneRewardId"),
  card_id: z.string().describe("StampCardId"),
  lane_id: z.string().describe("StampLaneId"),
}).describe("スタンプレーン報酬削除操作の詳細")

export type DeleteStampLaneRewardOperationValue = z.infer<typeof DeleteStampLaneRewardOperationValue>

// スタンプミッション

const CreateStampMissionOperationValue = z.object({
  stamp_mission_id: z.string().describe("StampMissionId"),
  card_id: z.string().describe("StampCardId"),
  name: z.string().describe("ミッション名"),
  kind: z.string().describe("ミッション種別"),
  award_stamps: z.number().describe("付与スタンプ数"),
  status: z.string().describe("ステータス"),
}).describe("スタンプミッション作成操作の詳細")

export type CreateStampMissionOperationValue = z.infer<typeof CreateStampMissionOperationValue>

const UpdateStampMissionOperationValue = z.object({
  stamp_mission_id: z.string().describe("StampMissionId"),
  card_id: z.string().describe("StampCardId"),
  name: z.string().optional().describe("ミッション名"),
  kind: z.string().optional().describe("ミッション種別"),
  award_stamps: z.number().optional().describe("付与スタンプ数"),
  status: z.string().optional().describe("ステータス"),
}).describe("スタンプミッション更新操作の詳細")

export type UpdateStampMissionOperationValue = z.infer<typeof UpdateStampMissionOperationValue>

const DeleteStampMissionOperationValue = z.object({
  stamp_mission_id: z.string().describe("StampMissionId"),
  card_id: z.string().describe("StampCardId"),
}).describe("スタンプミッション削除操作の詳細")

export type DeleteStampMissionOperationValue = z.infer<typeof DeleteStampMissionOperationValue>

const ForceUpdateStampMissionProgressOperationValue = z.object({
  user_id: z.string().describe("対象ユーザーID"),
  card_id: z.string().describe("StampCardId"),
  mission_id: z.string().describe("StampMissionId"),
  complete_status: z.string().describe("変更後の complete_status"),
  previous_complete_status: z.string().optional().describe("変更前の complete_status"),
  day_key: z.string().optional().describe("デイリーミッションの場合の対象日付キー"),
  stamp_count_delta: z.number().describe("スタンプ数の変動（+/- award_stamps）"),
}).describe("スタンプミッション進捗の強制変更操作の詳細")

export type ForceUpdateStampMissionProgressOperationValue = z.infer<typeof ForceUpdateStampMissionProgressOperationValue>

// 通報管理

const UpdateViolationReportOperationValue = z.object({
  violation_report_id: z.string().describe("ViolationReportId"),
  status: ViolationReportStatusSchema.optional().describe("変更後の通報ステータス"),
  memo: z.string().optional().describe("変更後の運営メモ"),
}).describe("通報ステータス・メモ更新操作の詳細")

export type UpdateViolationReportOperationValue = z.infer<typeof UpdateViolationReportOperationValue>

// ストーリーズピン留め

const PinnedStoryUserAuditContent = z.object({
  user_id: UserIdSchema.describe("ピン留め対象ユーザーのID"),
  audience: PinnedStoryUserAudience.describe("対象ユーザー"),
  priority: z.number().int().describe("優先度"),
  display_period: Period.describe("表示期間"),
  status: DocumentStatus.describe("ピン留めの状態"),
}).describe("ピン留めの監査用コンテンツ")

const CreatePinnedStoryUserOperationValue = z.object({
  pinned_story_user_id: z.string().describe("作成されたPinnedStoryUserId"),
  content: PinnedStoryUserAuditContent.describe("作成内容"),
}).describe("ストーリーズピン留め作成操作の詳細")

export type CreatePinnedStoryUserOperationValue = z.infer<typeof CreatePinnedStoryUserOperationValue>

const UpdatePinnedStoryUserOperationValue = z.object({
  pinned_story_user_id: z.string().describe("更新されたPinnedStoryUserId"),
  changes: PinnedStoryUserAuditContent.partial().describe("変更されたフィールドのみ"),
}).describe("ストーリーズピン留め更新操作の詳細")

export type UpdatePinnedStoryUserOperationValue = z.infer<typeof UpdatePinnedStoryUserOperationValue>

const DeletePinnedStoryUserOperationValue = z.object({
  pinned_story_user_id: z.string().describe("削除されたPinnedStoryUserId"),
}).describe("ストーリーズピン留め削除操作の詳細")

export type DeletePinnedStoryUserOperationValue = z.infer<typeof DeletePinnedStoryUserOperationValue>

// ライブジャンル

const LiveGenreAuditContent = z.object({
  label: z.string().describe("ジャンルのラベル"),
  available: z.boolean().describe("利用可能フラグ"),
  priority: z.number().describe("優先度"),
}).describe("ライブジャンルの監査用コンテンツ")

const CreateLiveGenreOperationValue = z.object({
  content: LiveGenreAuditContent.describe("作成内容"),
}).describe("ライブジャンル作成操作の詳細")

export type CreateLiveGenreOperationValue = z.infer<typeof CreateLiveGenreOperationValue>

const UpdateLiveGenreOperationValue = z.object({
  live_genre_id: z.string().describe("LiveGenreId"),
  content: LiveGenreAuditContent.describe("更新後の全フィールド"),
}).describe("ライブジャンル更新操作の詳細")

export type UpdateLiveGenreOperationValue = z.infer<typeof UpdateLiveGenreOperationValue>

const UpdateLiveGenrePriorityOperationValue = z.object({
  live_genre_id: z.string().describe("LiveGenreId"),
  priority: z.number().describe("変更後の優先度"),
}).describe("ライブジャンルの優先度更新操作の詳細。一括更新リクエストでは対象ジャンル毎に1件ずつログを記録する。")

export type UpdateLiveGenrePriorityOperationValue = z.infer<typeof UpdateLiveGenrePriorityOperationValue>

// 定型ニックネーム / 二つ名 / 修飾語マスタ

const CreatePredefinedNicknameOperationValue = z.object({
  predefined_nickname_id: z.string().describe("作成されたPredefinedNicknameId"),
  value: z.string().describe("ニックネームの値"),
}).describe("定型ニックネーム作成操作の詳細")

export type CreatePredefinedNicknameOperationValue = z.infer<typeof CreatePredefinedNicknameOperationValue>

const DeletePredefinedNicknameOperationValue = z.object({
  predefined_nickname_id: z.string().describe("削除されたPredefinedNicknameId"),
}).describe("定型ニックネーム削除操作の詳細")

export type DeletePredefinedNicknameOperationValue = z.infer<typeof DeletePredefinedNicknameOperationValue>

const CreatePredefinedAliasOperationValue = z.object({
  predefined_alias_id: z.string().describe("作成されたPredefinedAliasId"),
  value: z.string().describe("二つ名の値"),
}).describe("定型二つ名作成操作の詳細")

export type CreatePredefinedAliasOperationValue = z.infer<typeof CreatePredefinedAliasOperationValue>

const DeletePredefinedAliasOperationValue = z.object({
  predefined_alias_id: z.string().describe("削除されたPredefinedAliasId"),
}).describe("定型二つ名削除操作の詳細")

export type DeletePredefinedAliasOperationValue = z.infer<typeof DeletePredefinedAliasOperationValue>

const CreatePredefinedAliasQualifierOperationValue = z.object({
  predefined_alias_qualifier_id: z.string().describe("作成されたPredefinedAliasQualifierId"),
  value: z.string().describe("修飾語の値"),
}).describe("定型修飾語作成操作の詳細")

export type CreatePredefinedAliasQualifierOperationValue = z.infer<typeof CreatePredefinedAliasQualifierOperationValue>

const DeletePredefinedAliasQualifierOperationValue = z.object({
  predefined_alias_qualifier_id: z.string().describe("削除されたPredefinedAliasQualifierId"),
}).describe("定型修飾語削除操作の詳細")

export type DeletePredefinedAliasQualifierOperationValue = z.infer<typeof DeletePredefinedAliasQualifierOperationValue>

// 管理者によるホロスーツ変更

const UpdateUserLookByAdminOperationValue = z.object({
  user_id: UserIdSchema,
  kind: z.enum(["DefaultLook", "Inventory", "VC"]).describe("ホロスーツの種別"),
  id: z.string().describe("ホロスーツID。DefaultLook / Inventory はアイテムID、VC は VRM 登録ドキュメントID。"),
}).describe("管理者によるユーザーホロスーツ変更操作の詳細")

export type UpdateUserLookByAdminOperationValue = z.infer<typeof UpdateUserLookByAdminOperationValue>

// 管理ツールからのフォロー操作

const CreateUserFollowOperationValue = z.object({
  user_id: UserIdSchema.describe("フォローするユーザー（user-follows コレクションを保持するユーザー）"),
  target_user_id: UserIdSchema.describe("フォロー対象のユーザー"),
}).describe("管理ツールからのフォロー作成操作の詳細")

export type CreateUserFollowOperationValue = z.infer<typeof CreateUserFollowOperationValue>

const DeleteUserFollowOperationValue = z.object({
  user_id: UserIdSchema.describe("フォローを解除するユーザー（user-follows コレクションを保持するユーザー）"),
  target_user_id: UserIdSchema.describe("フォロー解除対象のユーザー"),
}).describe("管理ツールからのフォロー解除操作の詳細")

export type DeleteUserFollowOperationValue = z.infer<typeof DeleteUserFollowOperationValue>

const UpdateFeatureFlagOperationValue = z.object({
  flag_id: z.string().describe("更新対象の FeatureFlag ドキュメントID"),
  public: z.boolean().describe("更新後の public 値"),
  previous_updated_at: z.number().nullable().describe("更新前のドキュメントの updated_at（ミリ秒）。ドキュメントが存在しなかった場合は null。前回の操作ログとの突き合わせに使用する"),
}).describe("機能フラグの公開状態を変更した操作の詳細")

export type UpdateFeatureFlagOperationValue = z.infer<typeof UpdateFeatureFlagOperationValue>

const AddFeatureFlagAllowedUserOperationValue = z.object({
  flag_id: z.string().describe("対象の FeatureFlag ドキュメントID"),
  user_id: z.string().describe("許可リストに追加したユーザーID"),
  note: z.string().optional().describe("追加時のメモ（任意）"),
}).describe("機能フラグ許可ユーザーを追加した操作の詳細")

export type AddFeatureFlagAllowedUserOperationValue = z.infer<typeof AddFeatureFlagAllowedUserOperationValue>

const RemoveFeatureFlagAllowedUserOperationValue = z.object({
  flag_id: z.string().describe("対象の FeatureFlag ドキュメントID"),
  user_id: z.string().describe("許可リストから削除したユーザーID"),
}).describe("機能フラグ許可ユーザーを削除した操作の詳細")

export type RemoveFeatureFlagAllowedUserOperationValue = z.infer<typeof RemoveFeatureFlagAllowedUserOperationValue>

// ユーザーインベントリ管理

const GrantUserInventoryByAdminOperationValue = z.object({
  user_id: UserIdSchema.describe("対象ユーザーのID"),
  item_id: ItemIdSchema.describe("付与したアイテムのID"),
}).describe("管理者によるユーザーインベントリ付与操作の詳細")

export type GrantUserInventoryByAdminOperationValue = z.infer<typeof GrantUserInventoryByAdminOperationValue>

const DeleteUserInventoryByAdminOperationValue = z.object({
  user_id: UserIdSchema.describe("対象ユーザーのID"),
  item_id: ItemIdSchema.describe("削除したアイテムのID"),
}).describe("管理者によるユーザーインベントリ削除操作の詳細")

export type DeleteUserInventoryByAdminOperationValue = z.infer<typeof DeleteUserInventoryByAdminOperationValue>

// VRM 運営指定NG

const BanUserVrmByOperatorOperationValue = z.object({
  user_id: UserIdSchema.describe("操作の起点となった対象ユーザーのID"),
  registration_id: z.string().describe("操作の起点となった VRM 登録ドキュメントの ID"),
  item_id: z.string().optional().describe("運営指定NGにした itemId（グローバルNGリストのドキュメントID）"),
  previous_status: z.string().optional().describe("設定前のバリデーションステータス"),
}).describe("管理者による VRM 運営指定NG 設定操作の詳細")

export type BanUserVrmByOperatorOperationValue = z.infer<typeof BanUserVrmByOperatorOperationValue>

const UnbanUserVrmByOperatorOperationValue = z.object({
  user_id: UserIdSchema.describe("操作の起点となった対象ユーザーのID"),
  registration_id: z.string().describe("操作の起点となった VRM 登録ドキュメントの ID"),
  item_id: z.string().optional().describe("運営指定NG解除した itemId（グローバルNGリストのドキュメントID）"),
  restored_status: z.string().optional().describe("復元先のバリデーションステータス"),
}).describe("管理者による VRM 運営指定NG 解除操作の詳細")

export type UnbanUserVrmByOperatorOperationValue = z.infer<typeof UnbanUserVrmByOperatorOperationValue>

const UnbanVrmByItemIdOperationValue = z.object({
  item_id: z.string().describe("運営指定NG解除した itemId（グローバルNGリストのドキュメントID）"),
}).describe("管理者による itemId 起点の VRM 運営指定NG 解除操作の詳細。登録ドキュメントに依存せず itemId のみで解除する。")

export type UnbanVrmByItemIdOperationValue = z.infer<typeof UnbanVrmByItemIdOperationValue>

// LIVEポイント付与タスク手動投入

const EnqueueGrantLivePointsOperationValue = z.object({
  enqueue_kind: z.enum(["all", "single"]).describe("投入モード: all=全件即時実行, single=LIVE指定実行"),
  live_id: z.string().optional().describe("対象のLiveId（single モードの場合）"),
  total_enqueued: z.number().describe("投入されたタスクの件数"),
}).describe("LIVEポイント付与タスク手動投入操作の詳細")

export type EnqueueGrantLivePointsOperationValue = z.infer<typeof EnqueueGrantLivePointsOperationValue>

// 期限付き無償コイン配布 手動実行

const EnqueueGrantExpiringFreeCoinsOperationValue = z.object({
  target_period: z.string().describe("対象期間のラベル"),
  total_enqueued: z.number().describe("投入されたタスクの件数"),
  enqueue_kind: z.enum(["all", "custom-period"]).describe("実行種別"),
  since_ms: z.number().optional().describe("集計開始日時（epoch ms）。custom-period の場合のみ"),
  until_ms: z.number().optional().describe("集計終了日時（epoch ms）。custom-period の場合のみ"),
}).describe("期限付き無償コイン配布 手動実行操作の詳細")

export type EnqueueGrantExpiringFreeCoinsOperationValue = z.infer<typeof EnqueueGrantExpiringFreeCoinsOperationValue>

const AdminOperationUser = z.object({
  id: z.string().describe("管理者の ID"),
  name: z.string().describe("操作時点の管理者の表示名"),
  email: z.string().describe("操作時点の管理者のメールアドレス"),
}).describe("操作を実行した管理者の情報（非正規化・操作時点のスナップショット）")

export type AdminOperationUser = z.infer<typeof AdminOperationUser>

export const AdminOperationDocumentBody = z.union([
  z.object({
    kind: z.literal(AdminOperationKind.updateUserProfile),
    value: UpdateUserProfileOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateUserStatus),
    value: UpdateUserStatusOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateUserVerified),
    value: UpdateUserVerifiedOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateUserIcon),
    value: UpdateUserIconOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteUserIcon),
    value: DeleteUserIconOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.forceWithdrawal),
    value: ForceWithdrawalOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteUserStory),
    value: DeleteUserStoryOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ブロック管理
  z.object({
    kind: z.literal(AdminOperationKind.blockUser),
    value: BlockUserOperationValue,
    target: AdminOperationTarget,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.unblockUser),
    value: UnblockUserOperationValue,
    target: AdminOperationTarget,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 管理者によるサブスクリプション付与
  z.object({
    kind: z.literal(AdminOperationKind.grantUserSubscriptionByAdmin),
    value: GrantUserSubscriptionByAdminOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // デフォルトホロスーツ
  z.object({
    kind: z.literal(AdminOperationKind.createInitialLook),
    value: CreateInitialLookOperationValue,
    target: AdminOperationTargetInitialLook,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateInitialLook),
    value: UpdateInitialLookOperationValue,
    target: AdminOperationTargetInitialLook,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteInitialLook),
    value: DeleteInitialLookOperationValue,
    target: AdminOperationTargetInitialLook,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ホームバナー
  z.object({
    kind: z.literal(AdminOperationKind.createHomeBanner),
    value: CreateHomeBannerOperationValue,
    target: AdminOperationTargetHomeBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateHomeBanner),
    value: UpdateHomeBannerOperationValue,
    target: AdminOperationTargetHomeBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteHomeBanner),
    value: DeleteHomeBannerOperationValue,
    target: AdminOperationTargetHomeBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ホームピックアップ
  z.object({
    kind: z.literal(AdminOperationKind.createHomePickup),
    value: CreateHomePickupOperationValue,
    target: AdminOperationTargetHomePickup,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteHomePickup),
    value: DeleteHomePickupOperationValue,
    target: AdminOperationTargetHomePickup,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ホームライブピン
  z.object({
    kind: z.literal(AdminOperationKind.createHomeLivePin),
    value: CreateHomeLivePinOperationValue,
    target: AdminOperationTargetHomeLivePin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateHomeLivePin),
    value: UpdateHomeLivePinOperationValue,
    target: AdminOperationTargetHomeLivePin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteHomeLivePin),
    value: DeleteHomeLivePinOperationValue,
    target: AdminOperationTargetHomeLivePin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 画像管理
  z.object({
    kind: z.literal(AdminOperationKind.createManagedImage),
    value: CreateManagedImageOperationValue,
    target: AdminOperationTargetManagedImage,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.replaceManagedImage),
    value: ReplaceManagedImageOperationValue,
    target: AdminOperationTargetManagedImage,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteManagedImage),
    value: DeleteManagedImageOperationValue,
    target: AdminOperationTargetManagedImage,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateManagedImageUsageTypes),
    value: UpdateManagedImageUsageTypesOperationValue,
    target: AdminOperationTargetManagedImage,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 抽選のお題
  z.object({
    kind: z.literal(AdminOperationKind.createTopicSuggestion),
    value: CreateTopicSuggestionOperationValue,
    target: AdminOperationTargetTopicSuggestion,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateTopicSuggestion),
    value: UpdateTopicSuggestionOperationValue,
    target: AdminOperationTargetTopicSuggestion,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteTopicSuggestion),
    value: DeleteTopicSuggestionOperationValue,
    target: AdminOperationTargetTopicSuggestion,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // NGワード
  z.object({
    kind: z.literal(AdminOperationKind.createGlobalNgWord),
    value: CreateGlobalNgWordOperationValue,
    target: AdminOperationTargetGlobalNgWord,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateGlobalNgWord),
    value: UpdateGlobalNgWordOperationValue,
    target: AdminOperationTargetGlobalNgWord,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteGlobalNgWord),
    value: DeleteGlobalNgWordOperationValue,
    target: AdminOperationTargetGlobalNgWord,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // パワー管理
  z.object({
    kind: z.literal(AdminOperationKind.createPower),
    value: CreatePowerOperationValue,
    target: AdminOperationTargetPower,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updatePower),
    value: UpdatePowerOperationValue,
    target: AdminOperationTargetPower,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deletePower),
    value: DeletePowerOperationValue,
    target: AdminOperationTargetPower,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // フォローしませんかユーザー
  z.object({
    kind: z.literal(AdminOperationKind.createRecommendedUser),
    value: CreateRecommendedUserOperationValue,
    target: AdminOperationTargetRecommendedUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteRecommendedUser),
    value: DeleteRecommendedUserOperationValue,
    target: AdminOperationTargetRecommendedUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 許可ドメイン
  z.object({
    kind: z.literal(AdminOperationKind.createAllowedDomain),
    value: CreateAllowedDomainOperationValue,
    target: AdminOperationTargetAllowedDomain,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteAllowedDomain),
    value: DeleteAllowedDomainOperationValue,
    target: AdminOperationTargetAllowedDomain,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 一括配信
  z.object({
    kind: z.literal(AdminOperationKind.createBulkDeliveryMaster),
    value: CreateBulkDeliveryMasterOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateBulkDeliveryMasterStatus),
    value: UpdateBulkDeliveryMasterStatusOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateBulkDeliveryMasterContent),
    value: UpdateBulkDeliveryMasterContentOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateBulkDeliveryMasterDeletion),
    value: UpdateBulkDeliveryMasterDeletionOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateBulkDeliveryMasterTargetSetting),
    value: UpdateBulkDeliveryMasterTargetSettingOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteBulkDeliveryMaster),
    value: DeleteBulkDeliveryMasterOperationValue,
    target: AdminOperationTargetBulkDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 新規ユーザー配布マスタ
  z.object({
    kind: z.literal(AdminOperationKind.createWelcomeDeliveryMaster),
    value: CreateWelcomeDeliveryMasterOperationValue,
    target: AdminOperationTargetWelcomeDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateWelcomeDeliveryMaster),
    value: UpdateWelcomeDeliveryMasterOperationValue,
    target: AdminOperationTargetWelcomeDeliveryMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // お知らせ
  z.object({
    kind: z.literal(AdminOperationKind.createSystemNotification),
    value: CreateSystemNotificationOperationValue,
    target: AdminOperationTargetSystemNotification,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSystemNotification),
    value: UpdateSystemNotificationOperationValue,
    target: AdminOperationTargetSystemNotification,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSystemNotification),
    value: DeleteSystemNotificationOperationValue,
    target: AdminOperationTargetSystemNotification,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ショップバナー
  z.object({
    kind: z.literal(AdminOperationKind.createShopBanner),
    value: CreateShopBannerOperationValue,
    target: AdminOperationTargetShopBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateShopBanner),
    value: UpdateShopBannerOperationValue,
    target: AdminOperationTargetShopBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteShopBanner),
    value: DeleteShopBannerOperationValue,
    target: AdminOperationTargetShopBanner,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ショップカスタムタグ
  z.object({
    kind: z.literal(AdminOperationKind.createShopCustomTag),
    value: CreateShopCustomTagOperationValue,
    target: AdminOperationTargetShopCustomTag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateShopCustomTag),
    value: UpdateShopCustomTagOperationValue,
    target: AdminOperationTargetShopCustomTag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteShopCustomTag),
    value: DeleteShopCustomTagOperationValue,
    target: AdminOperationTargetShopCustomTag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ショップTOP編成
  z.object({
    kind: z.literal(AdminOperationKind.updateShopTopCompositionFirst),
    value: UpdateShopTopCompositionFirstOperationValue,
    target: AdminOperationTargetShopTopComposition,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateShopTopCompositionList),
    value: UpdateShopTopCompositionListOperationValue,
    target: AdminOperationTargetShopTopComposition,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // GeoIP CIDR管理
  z.object({
    kind: z.literal(AdminOperationKind.createGeoIpCidrOverride),
    value: CreateGeoIpCidrOverrideOperationValue,
    target: AdminOperationTargetGeoIpCidr,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteGeoIpCidrOverride),
    value: DeleteGeoIpCidrOverrideOperationValue,
    target: AdminOperationTargetGeoIpCidr,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 管理者
  z.object({
    kind: z.literal(AdminOperationKind.registerAdmin),
    value: RegisterAdminOperationValue,
    target: AdminOperationTargetAdmin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateAdmin),
    value: UpdateAdminOperationValue,
    target: AdminOperationTargetAdmin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteAdmin),
    value: DeleteAdminOperationValue,
    target: AdminOperationTargetAdmin,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.inviteAdmin),
    value: InviteAdminOperationValue,
    target: AdminOperationTargetAdminInvite,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteAdminInvite),
    value: DeleteAdminInviteOperationValue,
    target: AdminOperationTargetAdminInvite,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // アプリ設定
  z.object({
    kind: z.literal(AdminOperationKind.setAppConfigRequirement),
    value: SetAppConfigRequirementOperationValue,
    target: AdminOperationTargetAppConfig,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteAppConfigRequirement),
    value: DeleteAppConfigRequirementOperationValue,
    target: AdminOperationTargetAppConfig,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.setAppConfigTermsAgreement),
    value: SetAppConfigTermsAgreementOperationValue,
    target: AdminOperationTargetAppConfig,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.setAppConfigGlobalNavigation),
    value: SetAppConfigGlobalNavigationOperationValue,
    target: AdminOperationTargetAppConfig,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 変換レート管理
  z.object({
    kind: z.literal(AdminOperationKind.setConversionRate),
    value: SetConversionRateOperationValue,
    target: AdminOperationTargetAppConfig,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ユーザー個別レート管理
  z.object({
    kind: z.literal(AdminOperationKind.setUserConversionRate),
    value: SetUserConversionRateOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteUserConversionRate),
    value: DeleteUserConversionRateOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // LIVEポイント加減算
  z.object({
    kind: z.literal(AdminOperationKind.updateLivePoints),
    value: UpdateLivePointsOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // LIVEポイント交換停止/解除
  z.object({
    kind: z.literal(AdminOperationKind.updateLivePointExchangeBlock),
    value: UpdateLivePointExchangeBlockOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // LIVEポイント変換ブロック（LIVE単位）
  z.object({
    kind: z.literal(AdminOperationKind.updateLivePointConversionBlock),
    value: UpdateLivePointConversionBlockOperationValue,
    target: AdminOperationTargetLive,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // メンテナンス管理
  z.object({
    kind: z.literal(AdminOperationKind.createMaintenanceSchedule),
    value: CreateMaintenanceScheduleOperationValue,
    target: AdminOperationTargetMaintenanceSchedule,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateMaintenanceSchedule),
    value: UpdateMaintenanceScheduleOperationValue,
    target: AdminOperationTargetMaintenanceSchedule,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteMaintenanceSchedule),
    value: DeleteMaintenanceScheduleOperationValue,
    target: AdminOperationTargetMaintenanceSchedule,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.duplicateMaintenanceSchedule),
    value: DuplicateMaintenanceScheduleOperationValue,
    target: AdminOperationTargetMaintenanceSchedule,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.createMaintenanceInternalUser),
    value: CreateMaintenanceInternalUserOperationValue,
    target: AdminOperationTargetMaintenanceInternalUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateMaintenanceInternalUser),
    value: UpdateMaintenanceInternalUserOperationValue,
    target: AdminOperationTargetMaintenanceInternalUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteMaintenanceInternalUser),
    value: DeleteMaintenanceInternalUserOperationValue,
    target: AdminOperationTargetMaintenanceInternalUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ライブコメント管理
  z.object({
    kind: z.literal(AdminOperationKind.deleteLiveComment),
    value: DeleteLiveCommentOperationValue,
    target: AdminOperationTargetLiveComment,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // お題投稿（抽選参加者）管理
  z.object({
    kind: z.literal(AdminOperationKind.deleteSelectionParticipant),
    value: DeleteSelectionParticipantOperationValue,
    target: AdminOperationTargetSelectionParticipant,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ブランド管理
  z.object({
    kind: z.literal(AdminOperationKind.createBrand),
    value: CreateBrandOperationValue,
    target: AdminOperationTargetBrand,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateBrand),
    value: UpdateBrandOperationValue,
    target: AdminOperationTargetBrand,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteBrand),
    value: DeleteBrandOperationValue,
    target: AdminOperationTargetBrand,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // アイテム管理
  z.object({
    kind: z.literal(AdminOperationKind.createItem),
    value: CreateItemOperationValue,
    target: AdminOperationTargetItem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateItem),
    value: UpdateItemOperationValue,
    target: AdminOperationTargetItem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteItem),
    value: DeleteItemOperationValue,
    target: AdminOperationTargetItem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // アイテム配信（販売期間）
  z.object({
    kind: z.literal(AdminOperationKind.createItemDistribution),
    value: CreateItemDistributionOperationValue,
    target: AdminOperationTargetItemDistribution,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateItemDistribution),
    value: UpdateItemDistributionOperationValue,
    target: AdminOperationTargetItemDistribution,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteItemDistribution),
    value: DeleteItemDistributionOperationValue,
    target: AdminOperationTargetItemDistribution,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // セール管理
  z.object({
    kind: z.literal(AdminOperationKind.createSale),
    value: CreateSaleOperationValue,
    target: AdminOperationTargetSale,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSale),
    value: UpdateSaleOperationValue,
    target: AdminOperationTargetSale,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSale),
    value: DeleteSaleOperationValue,
    target: AdminOperationTargetSale,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ショップ検索おすすめアイテム
  z.object({
    kind: z.literal(AdminOperationKind.updateShopSearchRecommendedItems),
    value: UpdateShopSearchRecommendedItemsOperationValue,
    target: AdminOperationTargetShopSearchRecommendedItems,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // システム運用 / 運用補助
  z.object({
    kind: z.literal(AdminOperationKind.updateSpaceThumbnailSetting),
    value: UpdateSpaceThumbnailSettingOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateVrmAccountImageSetting),
    value: UpdateVrmAccountImageSettingOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.requestToGenerateActiveSpaceThumbnails),
    value: RequestToGenerateActiveSpaceThumbnailsOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.requestToGenerateLiveThumbnails),
    value: RequestToGenerateLiveThumbnailsOperationValue,
    target: AdminOperationTargetLive,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.sendDebugPush),
    value: SendDebugPushOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.syncCoinBalanceCache),
    value: SyncCoinBalanceCacheOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.recoverAlgolia),
    value: RecoverAlgoliaOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スペース管理
  z.object({
    kind: z.literal(AdminOperationKind.createSpaceByAdmin),
    value: CreateSpaceByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSpaceByAdmin),
    value: UpdateSpaceByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSpaceByAdmin),
    value: DeleteSpaceByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.addUserToSpaceByAdmin),
    value: AddUserToSpaceByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.removeUserFromSpaceByAdmin),
    value: RemoveUserFromSpaceByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSpaceUserRoleByAdmin),
    value: UpdateSpaceUserRoleByAdminOperationValue,
    target: AdminOperationTargetSpace,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.stopLiveByAdmin),
    value: StopLiveByAdminOperationValue,
    target: AdminOperationTargetLive,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateLiveByAdmin),
    value: UpdateLiveByAdminOperationValue,
    target: AdminOperationTargetLive,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スペースの背景
  z.object({
    kind: z.literal(AdminOperationKind.createSpaceBackground),
    value: CreateSpaceBackgroundOperationValue,
    target: AdminOperationTargetSpaceBackground,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSpaceBackground),
    value: UpdateSpaceBackgroundOperationValue,
    target: AdminOperationTargetSpaceBackground,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSpaceBackground),
    value: DeleteSpaceBackgroundOperationValue,
    target: AdminOperationTargetSpaceBackground,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スペースのBGM
  z.object({
    kind: z.literal(AdminOperationKind.createSpaceBGM),
    value: CreateSpaceBGMOperationValue,
    target: AdminOperationTargetSpaceBGM,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSpaceBGM),
    value: UpdateSpaceBGMOperationValue,
    target: AdminOperationTargetSpaceBGM,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSpaceBGM),
    value: DeleteSpaceBGMOperationValue,
    target: AdminOperationTargetSpaceBGM,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スタンプカード
  z.object({
    kind: z.literal(AdminOperationKind.createStampCard),
    value: CreateStampCardOperationValue,
    target: AdminOperationTargetStampCard,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateStampCard),
    value: UpdateStampCardOperationValue,
    target: AdminOperationTargetStampCard,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteStampCard),
    value: DeleteStampCardOperationValue,
    target: AdminOperationTargetStampCard,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スタンプレーン
  z.object({
    kind: z.literal(AdminOperationKind.createStampLane),
    value: CreateStampLaneOperationValue,
    target: AdminOperationTargetStampLane,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateStampLane),
    value: UpdateStampLaneOperationValue,
    target: AdminOperationTargetStampLane,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteStampLane),
    value: DeleteStampLaneOperationValue,
    target: AdminOperationTargetStampLane,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スタンプレーン報酬
  z.object({
    kind: z.literal(AdminOperationKind.createStampLaneReward),
    value: CreateStampLaneRewardOperationValue,
    target: AdminOperationTargetStampLaneReward,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateStampLaneReward),
    value: UpdateStampLaneRewardOperationValue,
    target: AdminOperationTargetStampLaneReward,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteStampLaneReward),
    value: DeleteStampLaneRewardOperationValue,
    target: AdminOperationTargetStampLaneReward,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スタンプミッション
  z.object({
    kind: z.literal(AdminOperationKind.createStampMission),
    value: CreateStampMissionOperationValue,
    target: AdminOperationTargetStampMission,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateStampMission),
    value: UpdateStampMissionOperationValue,
    target: AdminOperationTargetStampMission,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteStampMission),
    value: DeleteStampMissionOperationValue,
    target: AdminOperationTargetStampMission,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // スタンプミッション進捗の強制変更
  z.object({
    kind: z.literal(AdminOperationKind.forceUpdateStampMissionProgress),
    value: ForceUpdateStampMissionProgressOperationValue,
    target: AdminOperationTargetStampMission,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // コイン管理
  z.object({
    kind: z.literal(AdminOperationKind.insertCoinPack),
    value: InsertCoinPackOperationValue,
    target: AdminOperationTargetCoinPack,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.revertCoinPack),
    value: RevertCoinPackOperationValue,
    target: AdminOperationTargetCoinPack,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.removeCoinPack),
    value: RemoveCoinPackOperationValue,
    target: AdminOperationTargetCoinPack,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateCoinPack),
    value: UpdateCoinPackOperationValue,
    target: AdminOperationTargetCoinPack,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // サブスクリプション
  z.object({
    kind: z.literal(AdminOperationKind.createSubscriptionPlan),
    value: CreateSubscriptionPlanOperationValue,
    target: AdminOperationTargetSubscriptionPlan,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSubscriptionPlan),
    value: UpdateSubscriptionPlanOperationValue,
    target: AdminOperationTargetSubscriptionPlan,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSubscriptionPlan),
    value: DeleteSubscriptionPlanOperationValue,
    target: AdminOperationTargetSubscriptionPlan,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.createSubscriptionProduct),
    value: CreateSubscriptionProductOperationValue,
    target: AdminOperationTargetSubscriptionProduct,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSubscriptionProduct),
    value: UpdateSubscriptionProductOperationValue,
    target: AdminOperationTargetSubscriptionProduct,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSubscriptionProduct),
    value: DeleteSubscriptionProductOperationValue,
    target: AdminOperationTargetSubscriptionProduct,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.cancelIABSubscriptions),
    value: CancelIABSubscriptionsOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // サブスクリプションアイテム付与マスタ
  z.object({
    kind: z.literal(AdminOperationKind.createSubscriptionItemGrantMaster),
    value: CreateSubscriptionItemGrantMasterOperationValue,
    target: AdminOperationTargetSubscriptionItemGrantMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateSubscriptionItemGrantMaster),
    value: UpdateSubscriptionItemGrantMasterOperationValue,
    target: AdminOperationTargetSubscriptionItemGrantMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteSubscriptionItemGrantMaster),
    value: DeleteSubscriptionItemGrantMasterOperationValue,
    target: AdminOperationTargetSubscriptionItemGrantMaster,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 通報管理
  z.object({
    kind: z.literal(AdminOperationKind.updateViolationReport),
    value: UpdateViolationReportOperationValue,
    target: AdminOperationTargetViolationReport,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ストーリーズピン留め
  z.object({
    kind: z.literal(AdminOperationKind.createPinnedStoryUser),
    value: CreatePinnedStoryUserOperationValue,
    target: AdminOperationTargetPinnedStoryUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updatePinnedStoryUser),
    value: UpdatePinnedStoryUserOperationValue,
    target: AdminOperationTargetPinnedStoryUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deletePinnedStoryUser),
    value: DeletePinnedStoryUserOperationValue,
    target: AdminOperationTargetPinnedStoryUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ライブジャンル
  z.object({
    kind: z.literal(AdminOperationKind.createLiveGenre),
    value: CreateLiveGenreOperationValue,
    target: AdminOperationTargetLiveGenre,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateLiveGenre),
    value: UpdateLiveGenreOperationValue,
    target: AdminOperationTargetLiveGenre,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.updateLiveGenrePriority),
    value: UpdateLiveGenrePriorityOperationValue,
    target: AdminOperationTargetLiveGenre,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時。一括更新リクエストでは対象ジャンル毎に1件ずつログを記録する。"),
  }),
  // 定型ニックネーム / 二つ名 / 修飾語マスタ
  z.object({
    kind: z.literal(AdminOperationKind.createPredefinedNickname),
    value: CreatePredefinedNicknameOperationValue,
    target: AdminOperationTargetPredefinedNickname,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deletePredefinedNickname),
    value: DeletePredefinedNicknameOperationValue,
    target: AdminOperationTargetPredefinedNickname,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.createPredefinedAlias),
    value: CreatePredefinedAliasOperationValue,
    target: AdminOperationTargetPredefinedAlias,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deletePredefinedAlias),
    value: DeletePredefinedAliasOperationValue,
    target: AdminOperationTargetPredefinedAlias,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.createPredefinedAliasQualifier),
    value: CreatePredefinedAliasQualifierOperationValue,
    target: AdminOperationTargetPredefinedAliasQualifier,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deletePredefinedAliasQualifier),
    value: DeletePredefinedAliasQualifierOperationValue,
    target: AdminOperationTargetPredefinedAliasQualifier,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 管理ツールからのフォロー操作
  // 管理者によるホロスーツ変更
  z.object({
    kind: z.literal(AdminOperationKind.updateUserLookByAdmin),
    value: UpdateUserLookByAdminOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.createUserFollow),
    value: CreateUserFollowOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteUserFollow),
    value: DeleteUserFollowOperationValue,
    target: AdminOperationTargetUser,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // 機能フラグ
  z.object({
    kind: z.literal(AdminOperationKind.updateFeatureFlag),
    value: UpdateFeatureFlagOperationValue,
    target: AdminOperationTargetFeatureFlag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.addFeatureFlagAllowedUser),
    value: AddFeatureFlagAllowedUserOperationValue,
    target: AdminOperationTargetFeatureFlag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.removeFeatureFlagAllowedUser),
    value: RemoveFeatureFlagAllowedUserOperationValue,
    target: AdminOperationTargetFeatureFlag,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  // ユーザーインベントリ管理
  z.object({
    kind: z.literal(AdminOperationKind.grantUserInventoryByAdmin),
    value: GrantUserInventoryByAdminOperationValue,
    target: AdminOperationTargetUserInventory,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.deleteUserInventoryByAdmin),
    value: DeleteUserInventoryByAdminOperationValue,
    target: AdminOperationTargetUserInventory,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),

  // VRM 運営指定NG
  z.object({
    kind: z.literal(AdminOperationKind.banUserVrmByOperator),
    value: BanUserVrmByOperatorOperationValue,
    target: AdminOperationTargetVrmRegistration,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.unbanUserVrmByOperator),
    value: UnbanUserVrmByOperatorOperationValue,
    target: AdminOperationTargetVrmRegistration,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),
  z.object({
    kind: z.literal(AdminOperationKind.unbanVrmByItemId),
    value: UnbanVrmByItemIdOperationValue,
    target: AdminOperationTargetVrmNgItem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),

  // LIVEポイント付与タスク手動投入
  z.object({
    kind: z.literal(AdminOperationKind.enqueueGrantLivePoints),
    value: EnqueueGrantLivePointsOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),

  // 期限付き無償コイン配布 手動実行
  z.object({
    kind: z.literal(AdminOperationKind.enqueueGrantExpiringFreeCoins),
    value: EnqueueGrantExpiringFreeCoinsOperationValue,
    target: AdminOperationTargetSystem,
    admin_user: AdminOperationUser,
    created_at: z.number().describe("操作日時"),
  }),

]).describe("管理者操作ログ。kind-value パターンで様々な監査ログを格納する。")

export type AdminOperationDocumentBody = z.infer<typeof AdminOperationDocumentBody>

export type AdminOperationDocument = AdminOperationDocumentBody & {
  id: AdminOperationId
}

export class AdminOperationDocuments {
  static toDocumentData(doc: AdminOperationDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): AdminOperationDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: doc.id as AdminOperationId,
      kind: data["kind"],
      value: data["value"],
      target: data["target"],
      admin_user: data["admin_user"],
      created_at: data["created_at"],
    }
  }
}
